import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
    LayoutDashboard,
    Package,
    Search,
    ShoppingBag,
    Settings,
    ArrowLeft,
    Plus,
    Edit,
    Trash2,
    Eye,
    TrendingUp,
    DollarSign,
    Upload,
    FileText,
    Truck,
    Copy,
    Layers,
    Image as ImageIcon,
    ArrowDownRight,
    ArrowUpRight,
    Power,
    AlertTriangle,
    EyeOff,
    RefreshCcw,
    Check,
    X,
    Store,
    Clock,
    User,
    ShoppingCart,
    ChevronDown,
    Filter,
    Info,
    Maximize2,
    Grid,
    MapPin,
    Gift,
    Printer,
    Zap
} from "lucide-react";
import axios from "axios";
import AddressFormGroup from "../components/AddressFormGroup";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SellerDashboardPage() {
    const { storeId } = useParams();
    const navigate = useNavigate();
    const [store, setStore] = useState(null);
    const [stats, setStats] = useState(null);
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [returns, setReturns] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // Settings State
    const [settingsForm, setSettingsForm] = useState({});
    const [deliverySettings, setDeliverySettings] = useState({
        base_price: 100,
        price_per_lb: 50,
        price_per_km: 0
    });
    const [savingSettings, setSavingSettings] = useState(false);
    const [showLocationEdit, setShowLocationEdit] = useState(false);

    // Product Management State (Wizard)
    const [dialogOpen, setDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [productType, setProductType] = useState("simple"); // simple, variable, resell
    const [isEditing, setIsEditing] = useState(false);
    const [editingProductId, setEditingProductId] = useState(null);

    // Order Management State
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
    const [trackingNumber, setTrackingNumber] = useState("");
    const [selectedItemForTracking, setSelectedItemForTracking] = useState(null);

    // Returns Management State
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [returnProcessOpen, setReturnProcessOpen] = useState(false);
    const [returnAction, setReturnAction] = useState("ACCEPT"); // ACCEPT, PARTIAL_REFUND, REJECT
    const [returnAmount, setReturnAmount] = useState("");
    const [returnReason, setReturnReason] = useState("");

    // Disputes (Litiges) Management State
    const [sellerDisputes, setSellerDisputes] = useState([]);
    const [selectedDisputeForReply, setSelectedDisputeForReply] = useState(null);
    const [disputeReplyOpen, setDisputeReplyOpen] = useState(false);
    const [disputeSellerResponse, setDisputeSellerResponse] = useState("");
    const [disputeSellerEvidenceUrls, setDisputeSellerEvidenceUrls] = useState([""]);
    const [disputeReplyLoading, setDisputeReplyLoading] = useState(false);

    // Product Form Data
    const [productForm, setProductForm] = useState({
        title: "",
        description: "",
        price: "",
        discount_price: "",
        cost_price: "",
        stock_quantity: "",
        min_wholesale_quantity: "",
        category_id: "",
        images: [],
        shipping_type: "STORE_STANDARD",
        weight: "",
        shipping_price: "",
        allow_pickup: false,
        allow_express: false,
        has_variants: false,
        attributes: [],
        variants: [],
        is_resell: false,
        original_product_id: null,
        return_policy_days: 0,
        tax_rate: ""
    });

    // MOQ Calculation for Supplier stores
    const isSupplierStore = store?.store_type?.toUpperCase() === 'SUPPLIER';

    const calculateMOQ = (price) => {
        const p = parseFloat(price);
        if (isNaN(p) || p <= 0) return null;
        if (p <= 50) return 1000;
        if (p <= 100) return 800;
        if (p <= 500) return 500;
        if (p <= 1000) return 450;
        if (p <= 2500) return 200;
        if (p <= 5000) return 50;
        return null; // Manual entry
    };

    // Auto-update MOQ when price changes for supplier stores
    // Only auto-fill if the user hasn't manually entered a value
    useEffect(() => {
        if (!isSupplierStore) return;
        setProductForm(prev => {
            // Don't overwrite if seller typed something manually
            if (prev.min_wholesale_quantity !== "" && prev.min_wholesale_quantity !== null) return prev;
            const moq = calculateMOQ(prev.price);
            return moq !== null ? { ...prev, min_wholesale_quantity: moq } : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productForm.price, isSupplierStore]);

    // Resell Catalog State
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [loadingSupplier, setLoadingSupplier] = useState(false);
    const [selectedSupplierProduct, setSelectedSupplierProduct] = useState(null);
    const [overrideShipping, setOverrideShipping] = useState(false);
    const [supplierSearch, setSupplierSearch] = useState("");
    const [supplierCategory, setSupplierCategory] = useState("all");
    const [viewProduct, setViewProduct] = useState(null);

    const handleViewDetails = (product) => {
        setViewProduct(product);
    };

    // Variant Generator State
    const [attrName, setAttrName] = useState("");
    const [attrValues, setAttrValues] = useState("");

    // Shipping Estimate State
    const [shippingEstimate, setShippingEstimate] = useState(null);
    // Variant Images State
    const [variantImagesDialogOpen, setVariantImagesDialogOpen] = useState(false);
    const [currentVariantIndex, setCurrentVariantIndex] = useState(null);
    const [currentVariantImages, setCurrentVariantImages] = useState([]);
    const [tempVariantImageUrl, setTempVariantImageUrl] = useState("");

    const [tempImageUrl, setTempImageUrl] = useState("");

    const fetchData = React.useCallback(async () => {
        try {
            const token = localStorage.getItem("buymoreeasy_token");
            const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

            // B02 fix: Fetch returns separately so its failure doesn't crash all dashboard data
            const [storeRes, productsRes, categoriesRes, ordersRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/stores/${storeId}`, authHeaders),
                axios.get(`${API_URL}/products?store_id=${storeId}`),
                axios.get(`${API_URL}/categories`),
                axios.get(`${API_URL}/store-orders`, authHeaders),
                axios.get(`${API_URL}/stores/${storeId}/stats`, authHeaders),
            ]);

            setStore(storeRes.data);
            setProducts(productsRes.data);
            setCategories(categoriesRes.data);

            // Backend already filters by vendor, but keep extra safety filter
            const storeOrders = (ordersRes.data || []).filter(order =>
                order.items.some(item => item.store_id === storeId)
            );
            setOrders(storeOrders);
            setStats(statsRes.data);

            // B02 fix: Returns fetched independently — failure is non-blocking
            try {
                const returnsRes = await axios.get(`${API_URL}/returns/store/${storeId}`, authHeaders);
                setReturns(returnsRes.data || []);
            } catch (returnsError) {
                console.warn("Retours non disponibles:", returnsError?.response?.status, returnsError?.message);
                setReturns([]); // Graceful fallback
            }

            // Disputes fetch — non-blocking
            try {
                const disputesRes = await axios.get(`${API_URL}/store-orders/disputes`, authHeaders);
                setSellerDisputes(disputesRes.data || []);
            } catch (disputesError) {
                console.warn("Litiges non disponibles:", disputesError?.response?.status, disputesError?.message);
                setSellerDisputes([]);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
            if (error.code === "ERR_NETWORK") {
                toast.error("Impossible de contacter le serveur. (Port 8002)");
            } else {
                toast.error("Erreur lors du chargement des données");
            }
        } finally {
            setLoading(false);
        }
    }, [storeId]);

    const handleOpenDisputeReply = (dispute) => {
        setSelectedDisputeForReply(dispute);
        setDisputeSellerResponse(dispute.seller_response || "");
        setDisputeSellerEvidenceUrls(dispute.seller_evidence_images?.length ? dispute.seller_evidence_images : [""]);
        setDisputeReplyOpen(true);
    };

    const handleSubmitDisputeReply = async () => {
        if (!selectedDisputeForReply || !disputeSellerResponse.trim()) return;
        setDisputeReplyLoading(true);
        try {
            const token = localStorage.getItem("buymoreeasy_token");
            const validImages = disputeSellerEvidenceUrls.filter(u => u.trim());
            await axios.post(
                `${API_URL}/store-orders/${selectedDisputeForReply.order_id}/dispute/respond`,
                { response: disputeSellerResponse, evidence_images: validImages },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Réponse envoyée ! L'administrateur va examiner le litige.");
            setDisputeReplyOpen(false);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'envoi de la réponse");
        } finally {
            setDisputeReplyLoading(false);
        }
    };




    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (store) {
            setSettingsForm({
                name: store.name || "",
                description: store.description || "",
                address: store.address || "",
                city: store.city || "",
                region: store.region || "",
                zipCode: store.zip_code || "",
                countryCode: store.country_code || "ht",
                address_details: store.address_details || "",
                lat: store.coordinates?.lat || null,
                lng: store.coordinates?.lng || store.coordinates?.lon || null,
                phone: store.phone || "",
                social_links: store.social_links || {},
                legal_docs_url: store.legal_docs_url || "",
                logo_url: store.logo_url || "",
                banner_url: store.banner_url || "",
                delivery_criteria: store.delivery_criteria || {
                    base_price: 100,
                    price_per_lb: 50,
                    price_per_km: 0,
                    marketing_zone_radius: 0,
                    free_delivery_radius: 0,
                    free_delivery_threshold: 0,
                    max_distance_km: 0,
                    allow_express: false,
                    express_marketing_zone_radius: 0,
                    express_price_per_lb: 75,
                    express_price_per_km: 0,
                    express_free_delivery_radius: 0,
                    express_free_delivery_threshold: 0,
                    express_max_distance_km: 0
                }
            });
            if (store.delivery_criteria) {
                setDeliverySettings(store.delivery_criteria);
            }
        }
    }, [store]);


    // --- Order Logic ---

    const handleOpenOrderDetails = (order) => {
        setSelectedOrder(order);
        setDetailsOpen(true);
    };

    const handleOpenTrackingDialog = (item) => {
        setSelectedItemForTracking(item);
        setTrackingNumber(item.tracking_number || "");
        setTrackingDialogOpen(true);
    };

    // Nouveau : vendor marque le colis prêt → mission TransporteurPro créée (ready)
    // SHIPPED sera déclenché automatiquement par le livreur lors du pickup
    const handleMarkReady = async (orderId) => {
        if (!window.confirm("Confirmer que le colis est prêt pour la collecte par le livreur ?")) return;
        try {
            const token = localStorage.getItem("buymoreeasy_token");
            await axios.post(`${API_URL}/store-orders/${orderId}/dispatch-to-deliverer`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Colis marqué prêt ! Le transporteur va assigner un livreur.");
            fetchData();
            setDetailsOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
        }
    };

    const handleSubmitTracking = async () => {
        if (!selectedItemForTracking) return;

        try {
            const token = localStorage.getItem("buymoreeasy_token");
            await axios.put(`${API_URL}/store-orders/items/${selectedItemForTracking.id}/status`, {
                status: "SHIPPED",
                tracking_number: trackingNumber
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Commande marquée comme expédiée !");
            setTrackingDialogOpen(false);
            setDetailsOpen(false); // Close details to refresh or keep open and refresh?
            fetchData();
        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const handlePrintDeliverySlip = () => {
        if (!selectedOrder) return;
        const token = localStorage.getItem('buymoreeasy_token');
        const url = `${API_URL}/store-orders/${selectedOrder.id}/label?store_id=${storeId}&token=${token}`;
        window.open(url, '_blank');
    };

    const handleOpenReturnProcess = (ret) => {
        setSelectedReturn(ret);
        setReturnAction("ACCEPT");
        setReturnAmount("");
        setReturnReason("");
        setReturnProcessOpen(true);
    };

    const handleProcessReturn = async () => {
        if (!selectedReturn) return;

        try {
            const payload = { action: returnAction };
            if (returnAction === "PARTIAL_REFUND") {
                if (!returnAmount || parseFloat(returnAmount) <= 0) {
                    toast.error("Veuillez saisir un montant de remboursement valide.");
                    return;
                }
                payload.refund_amount = parseFloat(returnAmount);
            } else if (returnAction === "REJECT") {
                if (!returnReason.trim()) {
                    toast.error("Veuillez saisir un motif pour le refus (litige).");
                    return;
                }
                payload.reason = returnReason;
            }

            const token = localStorage.getItem("buymoreeasy_token");
            await axios.post(
                `${API_URL}/returns/${selectedReturn.return_code}/process`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success("Retour traité avec succès !");
            setReturnProcessOpen(false);
            fetchData();
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.detail || "Erreur lors du traitement du retour");
        }
    };


    // --- Settings Functions ---
    const handleSaveSettings = async () => {
        if (!settingsForm.lat || !settingsForm.lng) {
            toast.error("Veuillez utiliser la carte pour définir l'adresse exacte (obligatoire pour la livraison).");
            return;
        }

        setSavingSettings(true);
        try {
            const updateData = {
                ...settingsForm,
                country_code: settingsForm.countryCode,
                zip_code: settingsForm.zipCode
            };
            if (settingsForm.lat && settingsForm.lng) {
                updateData.coordinates = { lat: settingsForm.lat, lng: settingsForm.lng };
            }
            // Use PATCH to update store directly
            await axios.patch(`${API_URL}/stores/${storeId}`, updateData);
            toast.success("Paramètres sauvegardés !");
            fetchData(); // Refresh
        } catch (error) {
            toast.error("Erreur sauvegarde paramètres");
        } finally {
            setSavingSettings(false);
        }
    };

    // --- Supplier Catalog Logic ---
    const fetchSupplierProducts = React.useCallback(async () => {
        setLoadingSupplier(true);
        try {
            let url = `${API_URL}/supplier/products?`;
            if (supplierSearch) url += `search=${supplierSearch}&`;
            if (supplierCategory && supplierCategory !== "all") url += `category_id=${supplierCategory}&`;

            const res = await axios.get(url);
            setSupplierProducts(res.data);
        } catch (e) {
            toast.error("Impossible de charger le catalogue fournisseur");
        } finally {
            setLoadingSupplier(false);
        }
    }, [supplierSearch, supplierCategory]);

    useEffect(() => {
        if (productType === 'resell' && currentStep === 2) {
            const timeoutId = setTimeout(() => {
                fetchSupplierProducts();
            }, 500); // Debounce search
            return () => clearTimeout(timeoutId);
        }
    }, [productType, currentStep, supplierSearch, supplierCategory, fetchSupplierProducts]);

    const handleSelectSupplierProduct = (p) => {
        setSelectedSupplierProduct(p);
        setProductForm({
            ...productForm,
            title: p.title,
            description: p.description,
            images: p.images,
            category_id: p.category?.id || "",
            stock_quantity: p.stock_quantity,
            weight: p.delivery_settings?.weight || "",
            is_resell: true,
            original_product_id: p.id,
            supplier_store_id: p.store_id,
            cost_price: p.wholesale_price || p.price || 0, // Automatically set cost price to supplier's wholesale or retail price
            price: "",
            // Dropship defaults: use supplier settings initially
            shipping_type: p.delivery_settings?.type || "STORE_STANDARD",
            shipping_price: p.delivery_settings?.price || ""
        });
        setOverrideShipping(false);
        setCurrentStep(3);
    };

    // --- Product Functions ---

    // --- Helper Functions for Wizard ---

    const handleAddImage = () => {
        if (tempImageUrl) {
            setProductForm(prev => ({ ...prev, images: [...prev.images, tempImageUrl] }));
            setTempImageUrl("");
        }
    };

    const handleRemoveImage = (index) => {
        setProductForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    };
    const handleFileUpload = async (e, target = "main") => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        e.target.value = "";

        for (const file of files) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error(`${file.name} est trop volumineux (> 5MB)`);
                continue;
            }

            try {
                const formData = new FormData();
                formData.append("file", file);
                const folder = (target === "logo" || target === "banner") ? "stores"
                    : (target === "legal_docs") ? "documents"
                    : "products";
                const res = await axios.post(`${API_URL}/uploads/?folder=${folder}`, formData);
                const url = res.data.url;

                if (target === "main") {
                    setProductForm(prev => {
                        if (prev.images.length >= 5) {
                            toast.error("Maximum 5 photos autorisées");
                            return prev;
                        }
                        return { ...prev, images: [...prev.images, url] };
                    });
                } else if (target === "variant") {
                    setCurrentVariantImages(prev => {
                        if (prev.length >= 5) return prev;
                        const newImages = [...prev, url];
                        setProductForm(p => {
                            const updatedVariants = [...p.variants];
                            if (currentVariantIndex !== null && updatedVariants[currentVariantIndex]) {
                                updatedVariants[currentVariantIndex].images = newImages;
                            }
                            return { ...p, variants: updatedVariants };
                        });
                        return newImages;
                    });
                } else if (target === "logo") {
                    setSettingsForm(prev => ({ ...prev, logo_url: url }));
                } else if (target === "banner") {
                    setSettingsForm(prev => ({ ...prev, banner_url: url }));
                } else if (target === "legal_docs") {
                    setSettingsForm(prev => ({ ...prev, legal_docs_url: url }));
                }
            } catch (err) {
                toast.error(`Erreur lors du téléchargement de ${file.name}`);
            }
        }
    };
    useEffect(() => {
        const calculateMoresureEstimate = async () => {
            if (!productForm.weight) return;
            try {
                // Mock estimate for now if API not ready
                // const res = await axios.get(`${API_URL}/shipping/estimate?weight=${productForm.weight}&service_level=standard`);
                // setShippingEstimate(res.data);
                setShippingEstimate({ price: 150 + (parseFloat(productForm.weight) * 20), delivery_days: 2 });
            } catch (e) {
                console.error(e);
            }
        };

        if (["free_moresure", "paid_moresure"].includes(productForm.shipping_type) && productForm.weight) {
            calculateMoresureEstimate();
        } else {
            setShippingEstimate(null);
        }
    }, [productForm.weight, productForm.shipping_type]);

    const addAttribute = () => {
        if (!attrName || !attrValues) return;
        const values = attrValues.split(',').map(v => v.trim()).filter(v => v);
        if (values.length === 0) return;

        setProductForm(prev => ({
            ...prev,
            attributes: [...prev.attributes, { name: attrName, values }]
        }));
        setAttrName("");
        setAttrValues("");
    };

    const removeAttribute = (index) => {
        setProductForm(prev => ({
            ...prev,
            attributes: prev.attributes.filter((_, i) => i !== index)
        }));
    };

    const generateVariants = () => {
        if (productForm.attributes.length === 0) return;

        const cartesian = (...a) => a.reduce((a, b) => a.flatMap(d => b.map(e => [d, e].flat())));
        const valueArrays = productForm.attributes.map(a => a.values);
        const combinations = valueArrays.length > 1 ? cartesian(...valueArrays) : valueArrays[0].map(v => [v]);

        const existingVariants = productForm.variants || [];
        // Create a map of existing variants by attribute string key for easy lookup
        const existingMap = {};
        existingVariants.forEach(v => {
            const key = Object.entries(v.attributes).sort().map(([k, val]) => `${k}:${val}`).join('|');
            existingMap[key] = v;
        });

        const newVariants = combinations.map(combo => {
            const attributes = {};
            combo.forEach((val, idx) => {
                attributes[productForm.attributes[idx].name] = val;
            });

            const key = Object.entries(attributes).sort().map(([k, val]) => `${k}:${val}`).join('|');
            const existing = existingMap[key];

            if (existing) {
                return { ...existing, attributes }; // Keep existing data
            }

            return {
                attributes,
                price: parseFloat(productForm.price) || 0,
                stock: 0,
                weight: parseFloat(productForm.weight) || 0,
                sku: `${productForm.title.substring(0, 3).toUpperCase()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`,
                images: []
            };
        });

        setProductForm(prev => ({ ...prev, variants: newVariants }));
    };

    const updateVariant = (index, field, value) => {
        const updated = [...productForm.variants];
        // Allow raw string input for intermediate states (e.g. "10.")
        updated[index][field] = value;
        setProductForm(prev => ({ ...prev, variants: updated }));
    };
    const handleOpenVariantImages = (index) => {
        setCurrentVariantIndex(index);
        setCurrentVariantImages(productForm.variants[index].images || []);
        setVariantImagesDialogOpen(true);
    };

    const handleAddVariantImage = () => {
        if (tempVariantImageUrl && currentVariantImages.length < 5) {
            const newImages = [...currentVariantImages, tempVariantImageUrl];
            setCurrentVariantImages(newImages);

            const updatedVariants = [...productForm.variants];
            updatedVariants[currentVariantIndex].images = newImages;
            setProductForm(prev => ({ ...prev, variants: updatedVariants }));

            setTempVariantImageUrl("");
        }
    };

    const handleRemoveVariantImage = (imgIndex) => {
        const newImages = currentVariantImages.filter((_, i) => i !== imgIndex);
        setCurrentVariantImages(newImages);

        const updatedVariants = [...productForm.variants];
        updatedVariants[currentVariantIndex].images = newImages;
        setProductForm(prev => ({ ...prev, variants: updatedVariants }));
    };


    // Moved calculateMoresureEstimate inside useEffect


    const handleEditProduct = (p) => {
        setProductForm({
            title: p.title,
            description: p.description,
            price: p.price,
            discount_price: p.discount_price || "",
            stock_quantity: p.stock_quantity,
            cost_price: p.cost_price || "",
            min_wholesale_quantity: p.min_wholesale_quantity || "",   // ← load from DB
            wholesale_price: p.wholesale_price || "",                  // ← load from DB
            category_id: p.category_id || "",
            images: p.images || [],
            shipping_type: p.delivery_settings?.type || "STORE_STANDARD",
            weight: p.delivery_settings?.weight || "",
            shipping_price: p.delivery_settings?.price || "",
            allow_pickup: p.delivery_settings?.allow_pickup || false,
            allow_express: p.allowed_delivery_types?.includes("EXPRESS") || false,
            has_variants: p.has_variants,
            attributes: (() => {
                const attrs = p.specifications?.variants?.attributes || [];
                return attrs.map(name => {
                    const values = [...new Set(p.variants?.map(v => v.attributes[name]) || [])].filter(v => v);
                    return { name, values };
                });
            })() || [],
            variants: p.variants || [],
            is_resell: p.is_resell,
            original_product_id: p.original_product_id,
            return_policy_days: p.return_policy_days || 0,
            tax_rate: p.tax_rate || "",
            condition: p.condition || "NEW",
        });
        setProductType(p.is_resell ? 'resell' : (p.has_variants ? 'variable' : 'simple'));
        setIsEditing(true);
        setEditingProductId(p.id);
        setDialogOpen(true);
        if (p.is_resell) {
            setSelectedSupplierProduct({
                id: p.original_product_id,
                title: p.title,
                description: p.description,
                images: p.images,
                price: p.cost_price || p.wholesale_price || 0, // Fallback to avoid 0 if possible
                delivery_settings: p.delivery_settings,
            });
            setOverrideShipping(true); // Allow them to see and edit the current shipping settings easily
            setCurrentStep(3);
        } else {
            setCurrentStep(2);
        }
    };


    const handleCreateOrUpdateProduct = async () => {
        // Basic validation
        if (!productForm.title || !productForm.price) {
            toast.error("Veuillez remplir le titre et le prix");
            return;
        }

        setCreating(true);
        try {
            const delivery_settings = {
                type: productForm.shipping_type,
                weight: productType === 'resell' ? (parseFloat(selectedSupplierProduct?.delivery_settings?.weight) || parseFloat(productForm.weight) || 0) : (parseFloat(productForm.weight) || 0),
                price: parseFloat(productForm.shipping_price) || 0,
                allow_pickup: productForm.allow_pickup
            };
            if (productType === 'resell') {
                if (!productForm.price || parseFloat(productForm.price) <= 0) {
                    toast({
                        variant: "destructive",
                        title: "Erreur",
                        description: "Veuillez définir votre prix de vente (supérieur à 0)."
                    });
                    setCreating(false);
                    return;
                }
            }

            if (productForm.images.length === 0) {
                toast.error("Veuillez ajouter au moins une image principale");
                setCreating(false);
                return;
            }
            const weightCheck = delivery_settings.weight;
            if (['STORE_STANDARD', 'STORE_FREE_ZONE'].includes(productForm.shipping_type) && (!weightCheck || weightCheck <= 0)) {
                if (productType !== 'resell') {
                    toast.error("Le poids est requis pour le calcul de livraison standard");
                    setCreating(false);
                    return;
                }
            }
            if (productForm.shipping_type === 'CARRIER_PAID' && (!productForm.shipping_price || parseFloat(productForm.shipping_price) <= 0)) {
                toast.error("Le prix de livraison est requis pour ce mode");
                setCreating(false);
                return;
            }

            // Prepare allowed delivery types
            const allowed_delivery_types = [];
            if (['STORE_STANDARD', 'STORE_FREE_ZONE'].includes(productForm.shipping_type)) {
                allowed_delivery_types.push("STORE_DELIVERY");
            } else if (['CARRIER_PAID', 'CARRIER_MARKETING'].includes(productForm.shipping_type)) {
                allowed_delivery_types.push("TRANSPORT");
            }
            if (productForm.allow_pickup) {
                allowed_delivery_types.push("PICKUP");
            }
            if (productForm.allow_express) {
                allowed_delivery_types.push("EXPRESS");
            }

            // Prepare data payload
            const productData = {
                title: productForm.title,
                description: productForm.description,
                price: parseFloat(productForm.price),
                discount_price: productForm.discount_price ? parseFloat(productForm.discount_price) : null,
                cost_price: productForm.cost_price ? parseFloat(productForm.cost_price) : null,
                wholesale_price: productForm.wholesale_price ? parseFloat(productForm.wholesale_price) : null,
                min_wholesale_quantity: productForm.min_wholesale_quantity ? parseInt(productForm.min_wholesale_quantity) : 0,
                stock_quantity: parseInt(productForm.stock_quantity) || 0,
                category_id: productForm.category_id || null,
                images: productForm.images,
                has_variants: productType === 'variable',
                is_resell: productType === 'resell',
                original_product_id: productForm.original_product_id,
                return_policy_days: parseInt(productForm.return_policy_days) || 0,
                tax_rate: parseFloat(productForm.tax_rate) || 0.0,
                delivery_settings: delivery_settings,
                allowed_delivery_types: allowed_delivery_types,
                variants: productForm.variants,
                condition: productForm.condition || "NEW",
                product_type: "PHYSICAL",
            };

            if (productType === 'variable') {
                const totalStock = productForm.variants.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0);
                productData.stock_quantity = totalStock;
            }

            if (productType !== 'variable') {
                productData.variants = [];
                productData.has_variants = false;
            }

            let response;
            if (isEditing) {
                response = await axios.put(`${API_URL}/products/${editingProductId}`, productData);
                setProducts(products.map(p => p.id === editingProductId ? response.data : p));
                toast.success("Produit mis à jour !");
            } else {
                response = await axios.post(`${API_URL}/stores/${storeId}/products`, productData);
                setProducts([response.data, ...products]);
                toast.success("Produit créé avec succès !");
            }

            setDialogOpen(false);
            resetForm();
        } catch (error) {
            console.error("Product Save Error:", error);
            const msg = error.response?.data?.detail || "Erreur lors de l'opération";
            const displayMsg = Array.isArray(msg) ? msg.map(e => e.msg).join(', ') : msg;
            toast.error(displayMsg);
        } finally {
            setCreating(false);
        }
    };

    const resetForm = () => {
        setProductForm({
            title: "",
            description: "",
            price: "",
            discount_price: "",
            cost_price: "",
            wholesale_price: "",
            stock_quantity: "",
            category_id: "",
            images: [],
            shipping_type: "STORE_STANDARD",
            weight: "",
            shipping_price: "",
            allow_pickup: false,
            has_variants: false,
            attributes: [],
            variants: [],
            min_wholesale_quantity: "",
            is_resell: false,
            original_product_id: null,
            return_policy_days: 0,
            tax_rate: "",
            condition: "NEW",
        });
        setCurrentStep(1);
        setProductType("simple");
        setIsEditing(false);
        setEditingProductId(null);
        setSelectedSupplierProduct(null);
        setOverrideShipping(false);
    };

    const handleToggleProductStatus = async (product) => {
        try {
            const newStatus = product.status === 'ACTIVE' ? 'DRAFT' : 'ACTIVE';
            await axios.put(`${API_URL}/products/${product.id}`, { status: newStatus });
            toast.success(`Produit ${newStatus === 'ACTIVE' ? 'activé' : 'masqué'}`);
            fetchData();
        } catch (e) {
            const msg = e.response?.data?.detail || "Erreur lors de la mise à jour du statut";
            toast.error(msg);
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) return;
        try {
            await axios.delete(`${API_URL}/products/${productId}`);
            toast.success("Produit supprimé");
            setProducts(products.filter(p => p.id !== productId));
        } catch (e) {
            const msg = e.response?.data?.detail || "Erreur lors de la suppression";
            toast.error(msg);
        }
    };

    const handleDeleteStore = async () => {
        if (!window.confirm("ATTENTION: Êtes-vous sûr de vouloir supprimer définitivement votre boutique et tous ses produits ?")) return;

        const confirmName = window.prompt(`Tapez "${store.name}" pour confirmer :`);
        if (confirmName !== store.name) {
            toast.error("Nom incorrect, suppression annulée");
            return;
        }

        try {
            await axios.delete(`${API_URL}/stores/${storeId}`);
            toast.success("Boutique supprimée");
            navigate('/mes-boutiques');
        } catch (e) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const handleToggleStoreStatus = async () => {
        try {
            await axios.post(`${API_URL}/stores/${storeId}/vacation`);
            const isNowVacation = store.status === 'ACTIVE';
            toast.success(isNowVacation ? "Mode vacances activé — boutique masquée" : "Boutique réactivée");
            fetchData();
        } catch (e) {
            const msg = e.response?.data?.detail || "Erreur lors du changement de statut";
            toast.error(msg);
        }
    };

    const handleUpdateItemStatus = async (itemId, newStatus) => {
        // Confirmation pour les statuts irréversibles
        if (newStatus === 'DELIVERED') {
            if (!window.confirm(
                "Confirmer la livraison de cet article ?\n\n" +
                "Cette action déclenchera le processus de libération des fonds au vendeur après le délai de retour."
            )) return;
        }
        if (newStatus === 'SHIPPED') {
            if (!window.confirm("Confirmer l'expédition de cet article ?")) return;
        }
        try {
            const token = localStorage.getItem("buymoreeasy_token");
            await axios.put(`${API_URL}/store-orders/items/${itemId}/status`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Statut mis à jour");
            fetchData();
            // If viewing details, update local order state if necessary, but fetchData refreshes everything
            if (selectedOrder) {
                // Ideally refresh selectedOrder too
                // For now, simpler to close modal or assume fetchData updates 'orders' and we rely on that.
                // But selectedOrder is a copy. So we need to re-find it.
                // We'll let fetchData handle it, but we might need to re-set selectedOrder in useEffect or manual find
            }
        } catch (error) {
            console.error("Update Status Error:", error);
            const msg = error.response?.data?.detail || "Erreur lors de la mise à jour du statut";
            toast.error(msg);
        }
    };

    const handleRefuseItem = async (itemId) => {
        const reason = window.prompt("Raison du refus (optionnel) :");
        if (reason === null) return; // cancelled pop-up

        if (!window.confirm("Êtes-vous sûr de vouloir refuser/annuler cet article ? L'acheteur sera automatiquement remboursé sans possibilité de rétractation.")) return;

        try {
            const token = localStorage.getItem("buymoreeasy_token");
            await axios.post(`${API_URL}/store-orders/items/${itemId}/refuse`, { reason: reason || "Refusé par le vendeur" }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Article refusé et remboursé avec succès");
            fetchData();
        } catch (error) {
            console.error("Refuse Item Error:", error);
            const msg = error.response?.data?.detail || "Erreur lors de l'annulation de l'article";
            toast.error(msg);
        }
    };

    const handleDownloadLabel = async (orderId) => {
        try {
            const token = localStorage.getItem("buymoreeasy_token");
            const response = await axios.get(`${API_URL}/store-orders/${orderId}/label?store_id=${storeId}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Bon_Livraison_${selectedOrder.order_number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Download Label Error:", error);
            toast.error("Erreur lors de la génération du bon de livraison");
        }
    };


    // Update selectedOrder when orders change
    useEffect(() => {
        if (selectedOrder && orders.length > 0) {
            const updated = orders.find(o => o.id === selectedOrder.id);
            if (updated && updated !== selectedOrder) setSelectedOrder(updated);
        }
    }, [orders, selectedOrder]);


    if (loading || !store) return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;

    const handleNextStep = () => {
        if (currentStep === 1) {
            if (!productType) {
                toast.error("Veuillez sélectionner un type de produit");
                return;
            }
        }
        if (currentStep === 2) {
            if (!productForm.title) { toast.error("Le titre est requis"); return; }
            if (!productForm.price || parseFloat(productForm.price) <= 0) { toast.error("Le prix doit être valide"); return; }
            if (!productForm.category_id) { toast.error("La catégorie est requise"); return; }
        }
        if (currentStep === 3) {
            if (productForm.images.length === 0) {
                toast.error("Veuillez ajouter au moins une image principale");
                return;
            }
            const weight = parseFloat(productForm.weight);
            if (['STORE_STANDARD', 'STORE_FREE_ZONE'].includes(productForm.shipping_type) && (!weight || weight <= 0)) {
                toast.error("Le poids est requis pour le calcul de livraison standard");
                return;
            }
            if (productForm.shipping_type === 'CARRIER_PAID' && (!productForm.shipping_price || parseFloat(productForm.shipping_price) <= 0)) {
                toast.error("Le prix de livraison est requis pour ce mode");
                return;
            }
        }
        if (currentStep === 4 && productType === 'variable') {
            if (productForm.variants.length === 0) {
                toast.error("Veuillez générer au moins une variante");
                return;
            }
            // Optional: Check if variants have prices/stock? 
            // Let's at least check unique attributes logic if needed, but existing generation generic is usually ok.
        }
        setCurrentStep(prev => prev + 1);
    };

    return (
        <div className="min-h-screen bg-gray-50/50">
            <Navbar />

            <div className="flex flex-col h-[calc(100vh-64px)]">
                {/* Dashboard Header */}
                <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/mes-boutiques">
                            <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
                        </Link>
                        <div>
                            <h1 className="text-xl font-bold flex items-center gap-2">
                                <Store className="h-5 w-5 text-orange-600" />
                                {store.name}
                                <Badge variant="outline" className="ml-2">
                                    {store.store_type === 'SELLER' ? 'Vendeur' :
                                        store.store_type === 'SUPPLIER' ? 'Fournisseur' :
                                            store.store_type === 'ARTISAN' ? 'Artisan' : store.store_type}
                                </Badge>
                            </h1>
                            <p className="text-xs text-gray-500">Tableau de bord</p>
                        </div>
                        <Link to={`/boutique/${store.slug || store.id}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" className="ml-2 gap-2 border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800">
                                <Eye className="h-4 w-4" /> Voir ma boutique
                            </Button>
                        </Link>
                    </div>
                </header>

                {store.status === 'SUSPENDED' && (
                    <div className="bg-red-50 border-b-2 border-red-500 px-6 py-5 flex items-start gap-4">
                        <div className="bg-red-100 p-2 rounded-full shrink-0">
                            <AlertTriangle className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-red-800 text-base">⛔ Boutique suspendue par l'administration</h3>
                            <p className="text-sm text-red-700 mt-1">
                                Votre boutique <strong>{store.name}</strong> a été suspendue et n'est plus visible par les clients.
                                Vos produits ont été retirés de la liste publique.
                            </p>
                            <p className="text-sm text-red-600 mt-2">
                                Pour contester cette décision ou obtenir plus d'informations, contactez notre équipe :{" "}
                                <a href="mailto:support@buymoreeasy.com" className="underline font-semibold">support@buymoreeasy.com</a>
                            </p>
                        </div>
                    </div>
                )}

                {store.status === 'VACATION' && (
                    <div className="bg-blue-50 border-b border-blue-300 px-6 py-4 flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-blue-600 shrink-0" />
                        <div className="flex-1">
                            <h3 className="font-bold text-blue-800">Mode Vacances activé</h3>
                            <p className="text-sm text-blue-700">
                                Votre boutique est temporairement masquée. Désactivez le mode vacances dans <strong>Paramètres</strong> pour la rendre visible à nouveau.
                            </p>
                        </div>
                    </div>
                )}

                {store.status === 'REJECTED' && (
                    <div className="bg-red-50 border-b border-red-200 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                            <div>
                                <h3 className="font-bold text-red-800">Demande Rejetée</h3>
                                <p className="text-sm text-red-700">
                                    Raison : {store.rejection_reason || "Documents manquants ou invalides."}
                                    <br />
                                    <span className="text-xs opacity-75">Les frais d'activation ont été remboursés sur votre portefeuille. Veuillez corriger et payer à nouveau.</span>
                                </p>
                            </div>
                        </div>
                        <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => navigate(`/store-payment/${store.id}`)}>
                            Payer & Renvoyer
                        </Button>
                    </div>
                )}

                {/* Dashboard Content */}
                <div className="flex-1 p-6 overflow-auto">
                    <Tabs defaultValue="overview" className="space-y-6">
                        <TabsList className="bg-white p-1 border">
                            <TabsTrigger value="overview" className="gap-2"><LayoutDashboard className="h-4 w-4" /> Vue d&apos;ensemble</TabsTrigger>
                            <TabsTrigger value="products" className="gap-2"><Package className="h-4 w-4" /> Produits</TabsTrigger>
                            <TabsTrigger value="orders" className="gap-2"><ShoppingBag className="h-4 w-4" /> Commandes</TabsTrigger>
                            <TabsTrigger value="returns" className="gap-2"><RefreshCcw className="h-4 w-4" /> Retours</TabsTrigger>
                            <TabsTrigger value="litiges" className="gap-2">
                                <AlertTriangle className="h-4 w-4" /> Litiges
                                {sellerDisputes.filter(d => d.status === 'OPEN').length > 0 && (
                                    <Badge className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0">
                                        {sellerDisputes.filter(d => d.status === 'OPEN').length}
                                    </Badge>
                                )}
                            </TabsTrigger>
                            <TabsTrigger value="settings" className="gap-2"><Settings className="h-4 w-4" /> Paramètres</TabsTrigger>
                        </TabsList>

                        {/* OVERVIEW TAB - Same stats */}
                        <TabsContent value="overview">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium text-gray-500">Ventes Brutes</CardTitle>
                                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{(stats?.gross_sales ?? 0).toFixed(2)} HTG</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium text-gray-500">Remboursements</CardTitle>
                                        <ArrowDownRight className="h-4 w-4 text-red-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold text-red-600">{(stats?.refunds ?? 0).toFixed(2)} HTG</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium text-gray-500">Ventes Nettes</CardTitle>
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold text-green-600">{(stats?.net_sales ?? 0).toFixed(2)} HTG</div>
                                    </CardContent>
                                </Card>
                                <Card className="bg-green-50 border-green-200">
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium text-green-800">Marge Brute (Est.)</CardTitle>
                                        <ArrowUpRight className="h-4 w-4 text-green-600" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold text-green-900">{(stats?.gross_margin ?? 0).toFixed(2)} HTG</div>
                                        <p className="text-xs text-green-700">Basé sur coûts estimés</p>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        {/* PRODUCTS TAB - Simplified Wizard usage */}
                        <TabsContent value="products">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold">Inventaire</h2>
                                <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
                                    <Plus className="mr-2 h-4 w-4" /> Ajouter un produit
                                </Button>
                            </div>

                            <div className="rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Image</TableHead>
                                            <TableHead>Titre</TableHead>
                                            <TableHead>Prix</TableHead>
                                            <TableHead>Poids (lb)</TableHead>
                                            <TableHead>Stock</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {products.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center h-24 text-muted-foreground">
                                                    Aucun produit trouvé
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            products.map((product) => (
                                                <TableRow key={product.id} className="group hover:bg-gray-50 transition-colors">
                                                    <TableCell>
                                                        <div className="w-12 h-12 rounded bg-gray-100 overflow-hidden relative border border-gray-200">
                                                            {product.images && product.images.length > 0 ? (
                                                                <img
                                                                    src={product.images[0]}
                                                                    alt={product.title}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <Package className="w-6 h-6 m-3 text-gray-400" />
                                                            )}
                                                            {product.is_resell && (
                                                                <div className="absolute bottom-0 right-0 bg-blue-500 text-white p-0.5 rounded-tl shadow-sm" title="Produit Dropshipping">
                                                                    <RefreshCcw size={10} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-col gap-1">
                                                            <span className="font-semibold text-gray-800">{product.title}</span>
                                                            <div className="flex flex-wrap gap-2">
                                                                {product.has_variants && (
                                                                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 flex items-center gap-1 text-[10px] px-1.5 py-0 h-5">
                                                                        <Layers size={10} />
                                                                        Variantes: {product.variants ? product.variants.length : 'N/A'}
                                                                    </Badge>
                                                                )}
                                                                {product.is_resell && (
                                                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1 text-[10px] px-1.5 py-0 h-5">
                                                                        <RefreshCcw size={10} />
                                                                        Revente
                                                                    </Badge>
                                                                )}
                                                                {!product.has_variants && !product.is_resell && (
                                                                    <Badge variant="outline" className="text-gray-500 border-gray-200 text-[10px] px-1.5 py-0 h-5">
                                                                        Simple
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-col">
                                                            <span className="font-bold">{product.price} HTG</span>
                                                            {product.discount_price && (
                                                                <span className="text-xs text-red-500 line-through">
                                                                    {product.discount_price} HTG
                                                                </span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-xs text-gray-600">
                                                            {product.delivery_settings?.weight ?? product.specifications?.weight_lb ?? '0.5'}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className={`font-mono text-sm ${product.stock_quantity <= 5 ? "text-red-600 font-bold" : "text-gray-600"}`}>
                                                            {product.stock_quantity}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={product.status === 'ACTIVE' ? 'default' : 'secondary'} className={product.status === 'ACTIVE' ? "bg-green-100 text-green-800 hover:bg-green-200 border-green-200" : ""}>
                                                            {product.status === 'ACTIVE' ? 'Actif' : 'Brouillon'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleViewDetails(product)} title="Voir détails">
                                                                <Info className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-600 hover:text-gray-900" onClick={() => handleEditProduct(product)} title="Modifier">
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" onClick={() => handleToggleProductStatus(product)} title={product.status === 'ACTIVE' ? "Masquer" : "Publier"}>
                                                                {product.status === 'ACTIVE' ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
                                                            </Button>
                                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteProduct(product.id)} title="Supprimer">
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </TabsContent>

                        {/* ORDERS TAB - Enhanced */}
                        <TabsContent value="orders">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Commandes récentes</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>N° Commande</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Client</TableHead>
                                                <TableHead>Statut Local</TableHead>
                                                <TableHead>Total (Votre part)</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {orders.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center h-24 text-muted-foreground">Aucune commande</TableCell>
                                                </TableRow>
                                            ) : (
                                                orders.map((order) => {
                                                    // Filter items for this store
                                                    const storeItems = order.items.filter(i => i.store_id === storeId);
                                                    // Calculate total for this store only
                                                    const storeTotal = storeItems.reduce((acc, item) => acc + (item.unit_price * item.quantity), 0);

                                                    // Determine effective status for this store's portion
                                                    const isAllDelivered  = storeItems.every(i => i.delivery_status === 'DELIVERED');
                                                    const isAllShipped    = storeItems.every(i => ['SHIPPED', 'DELIVERED'].includes(i.delivery_status));
                                                    const isAllCancelled  = storeItems.every(i => ['CANCELLED', 'REFUNDED'].includes(i.delivery_status));
                                                    const payStatus       = (order.payment_status || '').toUpperCase();
                                                    const isPaid          = payStatus === 'PAID' || order.payment_method === 'cash_on_delivery';
                                                    const missionStatus   = order.shipping_info?.[storeId]?.transporteur_status;

                                                    let displayStatus = 'PENDING';
                                                    if (order.order_status === 'CANCELLED' || isAllCancelled) {
                                                        displayStatus = 'CANCELLED';
                                                    } else if (isAllDelivered) {
                                                        displayStatus = 'DELIVERED';
                                                    } else if (isAllShipped) {
                                                        displayStatus = 'SHIPPED';
                                                    } else if (!isPaid) {
                                                        displayStatus = 'UNPAID';
                                                    } else if (missionStatus === 'available') {
                                                        displayStatus = 'DISPATCHED';
                                                    } else if (missionStatus === 'accepted' || missionStatus === 'in_transit') {
                                                        displayStatus = 'IN_TRANSIT';
                                                    }

                                                    return (
                                                        <TableRow key={order.id}>
                                                            <TableCell className="font-medium">{order.order_number}</TableCell>
                                                            <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                                                            <TableCell>
                                                                <div className="flex flex-col">
                                                                    <span>{order.shipping_address?.name || 'Client'}</span>
                                                                    <span className="text-xs text-muted-foreground">{order.shipping_address?.phone}</span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge variant="outline" className={{
                                                                    'DELIVERED':  'bg-green-100 text-green-800 border-green-200',
                                                                    'SHIPPED':    'bg-blue-100 text-blue-800 border-blue-200',
                                                                    'IN_TRANSIT': 'bg-blue-100 text-blue-800 border-blue-200',
                                                                    'DISPATCHED': 'bg-indigo-100 text-indigo-800 border-indigo-200',
                                                                    'CANCELLED':  'bg-red-100 text-red-800 border-red-200',
                                                                    'UNPAID':     'bg-gray-100 text-gray-500 border-gray-200',
                                                                    'PENDING':    'bg-yellow-100 text-yellow-800 border-yellow-200',
                                                                }[displayStatus] || 'bg-yellow-100 text-yellow-800 border-yellow-200'}>
                                                                    {{
                                                                        'PENDING':    'À Traiter',
                                                                        'UNPAID':     'Non payée',
                                                                        'DISPATCHED': 'En attente livreur',
                                                                        'IN_TRANSIT': 'En route',
                                                                        'SHIPPED':    'Expédié',
                                                                        'DELIVERED':  'Livré',
                                                                        'CANCELLED':  'Annulée',
                                                                    }[displayStatus] || displayStatus}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell>{storeTotal.toFixed(2)} HTG</TableCell>
                                                            <TableCell className="text-right">
                                                                <Button size="sm" variant="outline" onClick={() => handleOpenOrderDetails(order)}>
                                                                    Gérer
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </TabsContent>


                        {/* RETURNS TAB */}
                        <TabsContent value="returns" className="space-y-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <RefreshCcw className="h-5 w-5 text-orange-600" />
                                        Retours Entrants
                                        {returns.filter(r => r.return_status === 'INITIATED').length > 0 && (
                                            <Badge className="bg-orange-500 text-white ml-2">
                                                {returns.filter(r => r.return_status === 'INITIATED').length} en attente
                                            </Badge>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {returns.length === 0 ? (
                                        <div className="text-center py-16 text-muted-foreground">
                                            <RefreshCcw className="h-10 w-10 mx-auto mb-3 opacity-30" />
                                            <p className="font-medium">Aucun retour entrant</p>
                                            <p className="text-sm mt-1">Les demandes de retour de vos clients apparaîtront ici.</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>N° Commande</TableHead>
                                                    <TableHead>Code Retour</TableHead>
                                                    <TableHead>Produit</TableHead>
                                                    <TableHead>Montant</TableHead>
                                                    <TableHead>Date d&apos;initiation</TableHead>
                                                    <TableHead>Statut Retour</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {returns.map((ret) => {
                                                    const statusConfig = {
                                                        INITIATED: { label: 'En Attente', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                                                        ACCEPTED: { label: 'Accepté ✓', className: 'bg-green-100 text-green-800 border-green-200' },
                                                        PARTIAL_OFFER: { label: 'Offre Partielle', className: 'bg-blue-100 text-blue-800 border-blue-200' },
                                                        DISPUTED: { label: 'En Litige', className: 'bg-red-100 text-red-800 border-red-200' },
                                                        COMPLETED: { label: 'Terminé', className: 'bg-gray-100 text-gray-800 border-gray-200' },
                                                    };
                                                    const sc = statusConfig[ret.return_status] || { label: ret.return_status, className: '' };
                                                    return (
                                                        <TableRow key={ret.id}>
                                                            <TableCell className="font-medium">{ret.order_number}</TableCell>
                                                            <TableCell><Badge variant="outline" className="font-mono text-orange-700">{ret.return_code}</Badge></TableCell>
                                                            <TableCell>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-8 h-8 rounded bg-gray-100 overflow-hidden flex-shrink-0">
                                                                        {ret.product_image ? <img src={ret.product_image} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 m-2 text-gray-400" />}
                                                                    </div>
                                                                    <span className="line-clamp-1 max-w-[150px]">{ret.product_title}</span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="font-semibold">{(ret.unit_price * ret.quantity).toFixed(2)} HTG</TableCell>
                                                            <TableCell>{ret.return_initiated_at ? new Date(ret.return_initiated_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                                                            <TableCell>
                                                                <Badge variant="outline" className={sc.className}>{sc.label}</Badge>
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                {ret.return_status === 'INITIATED' && (
                                                                    <Button size="sm" variant="default" onClick={() => handleOpenReturnProcess(ret)}>
                                                                        Traiter
                                                                    </Button>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>


                        {/* LITIGES TAB */}
                        <TabsContent value="litiges" className="space-y-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <AlertTriangle className="h-5 w-5 text-red-600" />
                                        Litiges Clients
                                        {sellerDisputes.filter(d => d.status === 'OPEN').length > 0 && (
                                            <Badge className="bg-red-500 text-white ml-2">
                                                {sellerDisputes.filter(d => d.status === 'OPEN').length} à traiter
                                            </Badge>
                                        )}
                                    </CardTitle>
                                    <p className="text-sm text-muted-foreground">
                                        Les litiges ouverts par vos clients. Vous devez répondre avant l'arbitrage administrateur.
                                    </p>
                                </CardHeader>
                                <CardContent>
                                    {sellerDisputes.length === 0 ? (
                                        <div className="text-center py-16 text-muted-foreground">
                                            <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-20" />
                                            <p className="font-medium">Aucun litige</p>
                                            <p className="text-sm mt-1">C'est une bonne nouvelle !</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>N° Commande</TableHead>
                                                    <TableHead>Client</TableHead>
                                                    <TableHead>Motif</TableHead>
                                                    <TableHead>Montant</TableHead>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead>Statut</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {sellerDisputes.map((disp) => {
                                                    const statusConfig = {
                                                        OPEN: { label: 'Ouvert — à répondre', className: 'bg-red-100 text-red-800 border-red-200' },
                                                        SELLER_RESPONDED: { label: 'Répondu — attente arbitrage', className: 'bg-blue-100 text-blue-800 border-blue-200' },
                                                        INVESTIGATION: { label: 'En investigation', className: 'bg-purple-100 text-purple-800 border-purple-200' },
                                                        RESOLVED: { label: 'Résolu ✅', className: 'bg-green-100 text-green-800 border-green-200' },
                                                        REJECTED: { label: 'Rejeté', className: 'bg-gray-100 text-gray-800 border-gray-200' },
                                                    };
                                                    const sc = statusConfig[disp.status] || { label: disp.status, className: '' };
                                                    return (
                                                        <TableRow key={disp.id} className={disp.status === 'OPEN' ? 'bg-red-50/50' : ''}>
                                                            <TableCell className="font-medium">{disp.order_number}</TableCell>
                                                            <TableCell>{disp.buyer_name}</TableCell>
                                                            <TableCell>
                                                                <div className="max-w-[160px]">
                                                                    <p className="font-semibold text-sm truncate">{disp.reason}</p>
                                                                    {disp.description && <p className="text-xs text-gray-500 line-clamp-2">{disp.description}</p>}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="font-semibold">{(disp.total_amount || 0).toFixed(2)} HTG</TableCell>
                                                            <TableCell className="text-sm">{new Date(disp.created_at).toLocaleDateString('fr-FR')}</TableCell>
                                                            <TableCell>
                                                                <Badge variant="outline" className={sc.className}>{sc.label}</Badge>
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                {(disp.status === 'OPEN' || disp.status === 'INVESTIGATION') && (
                                                                    <Button size="sm" variant="destructive" onClick={() => handleOpenDisputeReply(disp)}>
                                                                        Répondre
                                                                    </Button>
                                                                )}
                                                                {disp.status === 'SELLER_RESPONDED' && (
                                                                    <Button size="sm" variant="outline" onClick={() => handleOpenDisputeReply(disp)}>
                                                                        Voir / Modifier
                                                                    </Button>
                                                                )}
                                                                {disp.status === 'RESOLVED' && disp.admin_verdict && (
                                                                    <div className="text-xs text-right">
                                                                        {disp.admin_verdict === 'REFUND_CLIENT' && <span className="text-red-600 font-semibold">Remboursé client</span>}
                                                                        {disp.admin_verdict === 'RELEASE_SELLER' && <span className="text-green-600 font-semibold">En votre faveur ✅</span>}
                                                                        {disp.admin_verdict === 'PARTIAL' && <span className="text-orange-600 font-semibold">Partiel : {disp.partial_amount_seller || 0} HTG</span>}
                                                                    </div>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Seller Dispute Reply Dialog */}
                        <Dialog open={disputeReplyOpen} onOpenChange={setDisputeReplyOpen}>
                            <DialogContent className="max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2 text-red-700">
                                        <AlertTriangle className="h-5 w-5" /> Répondre au litige
                                    </DialogTitle>
                                </DialogHeader>
                                {selectedDisputeForReply && (
                                    <div className="space-y-4 py-2">
                                        {/* Buyer's claim summary */}
                                        <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                                            <p className="text-xs font-bold text-red-500 uppercase mb-1">Réclamation du client</p>
                                            <p className="font-semibold text-sm">{selectedDisputeForReply.reason}</p>
                                            {selectedDisputeForReply.description && (
                                                <p className="text-sm text-red-800 mt-1">{selectedDisputeForReply.description}</p>
                                            )}
                                            {selectedDisputeForReply.evidence_images?.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {selectedDisputeForReply.evidence_images.map((url, i) => (
                                                        <a key={i} href={url} target="_blank" rel="noreferrer"
                                                            className="text-xs text-blue-600 underline">Preuve client {i + 1}</a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="space-y-1.5">
                                            <Label className="font-semibold">Votre réponse <span className="text-red-500">*</span></Label>
                                            <Textarea
                                                value={disputeSellerResponse}
                                                onChange={e => setDisputeSellerResponse(e.target.value)}
                                                placeholder="Expliquez votre version des faits, ce qui s'est passé, pourquoi le client a tort ou raison..."
                                                rows={5}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="font-semibold">Vos preuves (URLs photos)</Label>
                                            {disputeSellerEvidenceUrls.map((url, idx) => (
                                                <div key={idx} className="flex gap-2">
                                                    <input
                                                        type="url"
                                                        className="flex-1 border rounded-md px-3 py-1.5 text-sm"
                                                        placeholder="https://..."
                                                        value={url}
                                                        onChange={e => {
                                                            const next = [...disputeSellerEvidenceUrls];
                                                            next[idx] = e.target.value;
                                                            setDisputeSellerEvidenceUrls(next);
                                                        }}
                                                    />
                                                    {disputeSellerEvidenceUrls.length > 1 && (
                                                        <Button variant="ghost" size="sm" className="text-red-500 px-2"
                                                            onClick={() => setDisputeSellerEvidenceUrls(prev => prev.filter((_, i) => i !== idx))}>
                                                            ×
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                            {disputeSellerEvidenceUrls.length < 5 && (
                                                <Button variant="outline" size="sm"
                                                    onClick={() => setDisputeSellerEvidenceUrls(prev => [...prev, ""])}
                                                    className="text-xs">+ Ajouter une photo</Button>
                                            )}
                                        </div>

                                        {/* Admin verdict if resolved */}
                                        {selectedDisputeForReply.status === 'RESOLVED' && selectedDisputeForReply.resolution_notes && (
                                            <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                                                <p className="text-xs font-bold text-purple-600 uppercase mb-1">⚖️ Décision Admin</p>
                                                <p className="text-sm text-purple-900">{selectedDisputeForReply.resolution_notes}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setDisputeReplyOpen(false)}>Fermer</Button>
                                    {selectedDisputeForReply?.status !== 'RESOLVED' && (
                                        <Button
                                            className="bg-red-600 hover:bg-red-700"
                                            onClick={handleSubmitDisputeReply}
                                            disabled={!disputeSellerResponse.trim() || disputeReplyLoading}
                                        >
                                            {disputeReplyLoading ? 'Envoi...' : 'Envoyer la réponse'}
                                        </Button>
                                    )}
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>


                        {/* SETTINGS TAB */}
                        <TabsContent value="settings" className="space-y-6">
                            {/* General Settings - Same as StoreDashboard */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Informations Générales</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label>Logo de la boutique</Label>
                                            <div className="flex gap-4 items-center">
                                                <div className="w-20 h-20 rounded-full border bg-gray-50 overflow-hidden flex-shrink-0">
                                                    {settingsForm.logo_url ? (
                                                        <img src={settingsForm.logo_url} alt="Logo" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <Store className="w-8 h-8 m-auto text-gray-300 mt-6" />
                                                    )}
                                                </div>
                                                <div className="flex gap-2 w-full">
                                                    <Input
                                                        placeholder="URL du logo"
                                                        value={settingsForm.logo_url || ''}
                                                        onChange={e => setSettingsForm({ ...settingsForm, logo_url: e.target.value })}
                                                    />
                                                    <div className="relative">
                                                        <input type="file" id="logo-upload" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, "logo")} />
                                                        <Button type="button" variant="outline" size="icon" onClick={() => document.getElementById('logo-upload').click()}>
                                                            <Upload className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Bannière</Label>
                                            <div className="flex gap-2 w-full">
                                                <Input
                                                    placeholder="URL de la bannière"
                                                    value={settingsForm.banner_url || ''}
                                                    onChange={e => setSettingsForm({ ...settingsForm, banner_url: e.target.value })}
                                                />
                                                <div className="relative">
                                                    <input type="file" id="banner-upload" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, "banner")} />
                                                    <Button type="button" variant="outline" size="icon" onClick={() => document.getElementById('banner-upload').click()}>
                                                        <Upload className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2"><Label>Nom</Label><Input value={settingsForm.name || ''} onChange={e => setSettingsForm({ ...settingsForm, name: e.target.value })} /></div>
                                        <div className="space-y-2"><Label>Téléphone</Label><Input value={settingsForm.phone || ''} onChange={e => setSettingsForm({ ...settingsForm, phone: e.target.value })} /></div>
                                        <div className="space-y-2 md:col-span-2"><Label>Description</Label><Textarea value={settingsForm.description || ''} onChange={e => setSettingsForm({ ...settingsForm, description: e.target.value })} /></div>
                                        <div className="space-y-4 md:col-span-2 pt-4 border-t">
                                            <div className="flex justify-between items-center">
                                                <Label className="text-base font-semibold">Localisation de la boutique</Label>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setShowLocationEdit(!showLocationEdit)}
                                                    className="text-primary hover:text-primary/80"
                                                >
                                                    {showLocationEdit ? (
                                                        <><X className="h-4 w-4 mr-1" /> Fermer</>
                                                    ) : (
                                                        <><Edit className="h-4 w-4 mr-1" /> Modifier la localisation</>
                                                    )}
                                                </Button>
                                            </div>

                                            {showLocationEdit ? (
                                                <div className="bg-gray-50 p-4 rounded-xl border animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <AddressFormGroup
                                                        addressData={{
                                                            countryCode: settingsForm.countryCode,
                                                            address: settingsForm.address,
                                                            city: settingsForm.city,
                                                            region: settingsForm.region,
                                                            zipCode: settingsForm.zipCode,
                                                            address_details: settingsForm.address_details,
                                                            lat: settingsForm.lat,
                                                            lng: settingsForm.lng
                                                        }}
                                                        onChange={(newData) => {
                                                            setSettingsForm(prev => ({
                                                                ...prev,
                                                                countryCode: newData.countryCode,
                                                                address: newData.address,
                                                                city: newData.city,
                                                                region: newData.region,
                                                                zipCode: newData.zipCode,
                                                                address_details: newData.address_details,
                                                                lat: newData.lat,
                                                                lng: newData.lng
                                                            }));
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50/50 p-3 rounded-lg border border-dashed">
                                                    <MapPin className="h-4 w-4 mt-0.5 text-gray-400" />
                                                    <div>
                                                        <p className="font-medium text-gray-900">{settingsForm.address || 'Aucune adresse renseignée'}</p>
                                                        <p className="text-xs text-gray-500">{settingsForm.city} {settingsForm.region} {settingsForm.countryCode}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* DELIVERY SETTINGS */}
                                    <div className="pt-6 border-t">
                                        <h3 className="text-lg font-semibold mb-4">Configuration Livraison (Standard Boutique)</h3>
                                        <p className="text-sm text-gray-500 mb-4">Attention: Laissez "Distance Max" à 0 si vous livrez sans limite de distance dans tout le pays.</p>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <Label>Distance Max de Livraison (km)</Label>
                                                <Input type="number" min="0" value={settingsForm.delivery_criteria?.max_distance_km ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, max_distance_km: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Zone livraison gratuite Sure More (km)</Label>
                                                <Input type="number" min="0" value={settingsForm.delivery_criteria?.marketing_zone_radius ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, marketing_zone_radius: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>% pris en charge par la boutique (Transporteur offert)</Label>
                                                <p className="text-xs text-gray-400">Ex: 50 = boutique couvre 50% du tarif Sure More, client paie 50%</p>
                                                <Input type="number" min="0" max="100" value={settingsForm.delivery_criteria?.marketing_zone_contribution_pct ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, marketing_zone_contribution_pct: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Prix par Livre (HTG)</Label>
                                                <Input type="number" min="0" value={settingsForm.delivery_criteria?.price_per_lb ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, price_per_lb: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Prix par KM (HTG)</Label>
                                                <Input type="number" min="0" value={settingsForm.delivery_criteria?.price_per_km ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, price_per_km: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Zone livraison gratuite (km)</Label>
                                                <Input type="number" min="0" value={settingsForm.delivery_criteria?.free_delivery_radius ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, free_delivery_radius: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Livraison Gratuite à partir de (HTG)</Label>
                                                <Input type="number" min="0" value={settingsForm.delivery_criteria?.free_delivery_threshold ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, free_delivery_threshold: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Délai de Livraison Standard (ex: 1 à 3 jours)</Label>
                                                <Input type="text" placeholder="1 à 3 jours" value={settingsForm.delivery_criteria?.std_delivery_time ?? ""} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, std_delivery_time: e.target.value } })} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* EXPRESS DELIVERY SETTINGS */}
                                    <div className="bg-orange-50/10 p-4 rounded-xl border border-orange-100/50 mt-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <div className="bg-orange-100 p-2 rounded-lg">
                                                    <Zap className="h-5 w-5 text-orange-600" />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900">Configuration Livraison Express (24h)</h3>
                                                    <p className="text-xs text-gray-500">Activez et configurez vos propres tarifs pour le service express.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-full border shadow-sm">
                                                <Label htmlFor="allow_express" className={`font-semibold text-sm ${(parseFloat(settingsForm.delivery_criteria?.price_per_lb ?? 0) > 0 && "price_per_km" in (settingsForm.delivery_criteria || {})) ? "cursor-pointer" : "cursor-not-allowed text-gray-400"}`}>Activer Express</Label>
                                                <Switch
                                                    id="allow_express"
                                                    disabled={!(parseFloat(settingsForm.delivery_criteria?.price_per_lb ?? 0) > 0 && "price_per_km" in (settingsForm.delivery_criteria || {}))}
                                                    checked={(parseFloat(settingsForm.delivery_criteria?.price_per_lb ?? 0) > 0 && "price_per_km" in (settingsForm.delivery_criteria || {})) ? (settingsForm.delivery_criteria?.allow_express ?? false) : false}
                                                    onCheckedChange={checked => setSettingsForm({
                                                        ...settingsForm,
                                                        delivery_criteria: {
                                                            ...settingsForm.delivery_criteria,
                                                            allow_express: checked
                                                        }
                                                    })}
                                                />
                                            </div>
                                        </div>

                                        {!(parseFloat(settingsForm.delivery_criteria?.price_per_lb ?? 0) > 0 && "price_per_km" in (settingsForm.delivery_criteria || {})) && (
                                            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                                                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                                <div>
                                                    <p className="text-sm font-semibold text-amber-800">Livraison Standard requise</p>
                                                    <p className="text-xs text-amber-700 mt-0.5">
                                                        Configurez d'abord la livraison standard pour activer l'Express.
                                                        {!(parseFloat(settingsForm.delivery_criteria?.price_per_lb ?? 0) > 0) && <span className="font-bold"> Prix par Livre {">"} 0</span>}
                                                        {!("price_per_km" in (settingsForm.delivery_criteria || {})) && <span className="font-bold">, Prix par KM défini</span>}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {settingsForm.delivery_criteria?.allow_express && (parseFloat(settingsForm.delivery_criteria?.price_per_lb ?? 0) > 0 && "price_per_km" in (settingsForm.delivery_criteria || {})) && (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                                                <div className="space-y-2">
                                                    <Label className="text-gray-700 font-medium">Zone livraison gratuite Sure More (km) express</Label>
                                                    <Input type="number" min="0" className="border-orange-100 focus:ring-orange-500" value={settingsForm.delivery_criteria?.express_marketing_zone_radius ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, express_marketing_zone_radius: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-gray-700">Prix par Livre (HTG)</Label>
                                                    <Input type="number" min="0" className="border-orange-100 focus:ring-orange-500" value={settingsForm.delivery_criteria?.express_price_per_lb ?? 75} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, express_price_per_lb: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-gray-700">Prix par KM (HTG)</Label>
                                                    <Input type="number" min="0" className="border-orange-100 focus:ring-orange-500" value={settingsForm.delivery_criteria?.express_price_per_km ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, express_price_per_km: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-gray-700">Zone gratuite Express (km)</Label>
                                                    <Input type="number" min="0" className="border-orange-100 focus:ring-orange-500" value={settingsForm.delivery_criteria?.express_free_delivery_radius ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, express_free_delivery_radius: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-gray-700">Gratuit si Total &gt; (HTG)</Label>
                                                    <Input type="number" min="0" className="border-orange-100 focus:ring-orange-500" value={settingsForm.delivery_criteria?.express_free_delivery_threshold ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, express_free_delivery_threshold: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-gray-700">Distance Max Express (km)</Label>
                                                    <Input type="number" min="0" className="border-orange-100 focus:ring-orange-500" value={settingsForm.delivery_criteria?.express_max_distance_km ?? 0} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, express_max_distance_km: e.target.value === '' ? 0 : parseFloat(e.target.value) } })} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-gray-700">Délai de Livraison Express (ex: 1 à 24H)</Label>
                                                    <Input type="text" placeholder="1 à 24H" className="border-orange-100 focus:ring-orange-500" value={settingsForm.delivery_criteria?.express_delivery_time ?? ""} onChange={e => setSettingsForm({ ...settingsForm, delivery_criteria: { ...settingsForm.delivery_criteria, express_delivery_time: e.target.value } })} />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                </CardContent>
                            </Card>

                            {/* DANGER ZONE */}
                            <Card className="border-red-200 bg-red-50/10">
                                <CardHeader>
                                    <CardTitle className="text-red-600">Zone de Danger</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-white">
                                        <div>
                                            <div className="font-bold text-red-800">Mode Vacances</div>
                                            <p className="text-sm text-red-600">Désactivez temporairement votre boutique. Vos produits ne seront plus visibles.</p>
                                        </div>
                                        <Button
                                            variant={store.status === 'ACTIVE' ? "destructive" : "outline"}
                                            onClick={handleToggleStoreStatus}
                                            disabled={store.status === 'SUSPENDED'}
                                        >
                                            {store.status === 'ACTIVE' ? "Activer mode vacances" : store.status === 'VACATION' ? "Réactiver la boutique" : "Action non disponible"}
                                        </Button>
                                    </div>
                                    <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-white">
                                        <div>
                                            <div className="font-bold text-red-800">Supprimer la boutique</div>
                                            <p className="text-sm text-red-600">Action irréversible. Toutes les données seront effacées.</p>
                                        </div>
                                        <Button variant="destructive" onClick={handleDeleteStore}>Supprimer définitivement</Button>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg md:static md:bg-transparent md:border-t-0 md:shadow-none md:p-0 z-50">
                                <div className="flex justify-end">
                                    <Button onClick={handleSaveSettings} disabled={savingSettings} size="lg" className="w-full md:w-auto shadow-xl">
                                        <Settings className="mr-2 h-4 w-4" />
                                        {savingSettings ? "Sauvegarde..." : "Sauvegarder les paramètres"}
                                    </Button>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* PRODUCT WIZARD DIALOG */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{isEditing ? "Modifier le produit" : "Ajouter un nouveau produit"}</DialogTitle>
                    </DialogHeader>

                    <div className="py-4">
                        {/* Variant Images Sub-Dialog */}
                        <Dialog open={variantImagesDialogOpen} onOpenChange={setVariantImagesDialogOpen}>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Images de la variante</DialogTitle></DialogHeader>
                                <div className="space-y-4">
                                    <div className="flex gap-2">
                                        <Input placeholder="URL image" value={tempVariantImageUrl} onChange={e => setTempVariantImageUrl(e.target.value)} disabled={currentVariantImages.length >= 5} />
                                        <Button onClick={handleAddVariantImage} disabled={currentVariantImages.length >= 5}>Ajouter URL</Button>
                                        <div className="relative">
                                            <input type="file" id="variant-image-upload" className="hidden" accept="image/png, image/jpeg, image/gif" onChange={(e) => handleFileUpload(e, "variant")} disabled={currentVariantImages.length >= 5} />
                                            <Button type="button" variant="secondary" disabled={currentVariantImages.length >= 5} onClick={() => document.getElementById('variant-image-upload').click()}>
                                                <Upload className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {currentVariantImages.map((img, idx) => (
                                            <div key={idx} className="relative aspect-square border rounded overflow-hidden group">
                                                <img src={img} alt="" className="w-full h-full object-cover" />
                                                <button onClick={() => handleRemoveVariantImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100"><X size={12} /></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>

                        {currentStep === 1 && (
                            <div className="space-y-4">
                                <Label className="text-lg">Type de produit</Label>
                                <RadioGroup value={productType} onValueChange={setProductType} className="grid grid-cols-2 gap-4">
                                    <div onClick={() => setProductType('simple')} className={`border p-4 rounded cursor-pointer ${productType === 'simple' ? 'border-orange-500 bg-orange-50' : ''}`}>
                                        <h3 className="font-bold">Produit Simple</h3>
                                        <p className="text-sm text-gray-500">Article unique</p>
                                    </div>
                                    <div onClick={() => setProductType('variable')} className={`border p-4 rounded cursor-pointer ${productType === 'variable' ? 'border-orange-500 bg-orange-50' : ''}`}>
                                        <h3 className="font-bold">Produit Variable</h3>
                                        <p className="text-sm text-gray-500">Avec tailles, couleurs...</p>
                                    </div>
                                    {store?.store_type?.toLowerCase() !== 'supplier' && (
                                        <div onClick={() => setProductType('resell')} className={`border p-4 rounded cursor-pointer ${productType === 'resell' ? 'border-orange-500 bg-orange-50' : ''}`}>
                                            <h3 className="font-bold">Revente (Dropshipping)</h3>
                                            <p className="text-sm text-gray-500">Produits fournisseurs</p>
                                        </div>
                                    )}
                                </RadioGroup>
                            </div>
                        )}

                        {currentStep === 2 && productType === 'resell' && (
                            <div className="space-y-4">
                                <div className="flex flex-col md:flex-row gap-4 mb-4">
                                    <div className="flex-1 relative">
                                        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                        <Input
                                            placeholder="Rechercher un produit..."
                                            value={supplierSearch}
                                            onChange={(e) => setSupplierSearch(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                    <Select value={supplierCategory} onValueChange={setSupplierCategory}>
                                        <SelectTrigger className="w-full md:w-[200px]">
                                            <SelectValue placeholder="Catégorie" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Toutes</SelectItem>
                                            {categories.map(cat => (
                                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <h3 className="text-lg font-semibold">Catalogue Fournisseur</h3>
                                {loadingSupplier ? (
                                    <div className="text-center py-8">Chargement du catalogue...</div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto p-1">
                                        {supplierProducts.length === 0 ? (
                                            <div className="col-span-3 text-center py-8 text-gray-500">Aucun produit trouvé.</div>
                                        ) : (
                                            supplierProducts.map(p => (
                                                <div key={p.id}
                                                    className={`border rounded-lg p-3 cursor-pointer hover:border-orange-500 transition-colors ${selectedSupplierProduct?.id === p.id ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200' : ''}`}
                                                    onClick={() => handleSelectSupplierProduct(p)}
                                                >
                                                    <div className="aspect-square bg-gray-100 rounded mb-2 overflow-hidden relative">
                                                        {p.images && p.images[0] ? (
                                                            <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <Package className="w-full h-full p-4 text-gray-300" />
                                                        )}
                                                        <Badge className="absolute top-1 right-1 bg-white hover:bg-white text-black shadow-sm">
                                                            {p.store?.name}
                                                        </Badge>
                                                    </div>
                                                    <h4 className="font-medium line-clamp-1">{p.title}</h4>
                                                    <div className="flex justify-between items-center mt-2">
                                                        <span className="text-sm font-bold text-orange-600">Coût: {p.wholesale_price || p.price} G</span>
                                                        <Badge variant="outline">Stock: {p.stock_quantity}</Badge>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {currentStep === 2 && productType !== 'resell' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Titre *</Label>
                                        <Input value={productForm.title} onChange={e => setProductForm({ ...productForm, title: e.target.value })} placeholder="Ex: T-Shirt Coton Bio" />
                                        <p className="text-xs text-gray-500">Un titre clair augmente vos ventes.</p>
                                    </div>
                                    <div className="space-y-2"><Label>Description</Label><Textarea value={productForm.description} onChange={e => setProductForm({ ...productForm, description: e.target.value })} /></div>
                                    <div className="space-y-2"><Label>Catégorie</Label>
                                        <Select value={productForm.category_id} onValueChange={(val) => setProductForm({ ...productForm, category_id: val })}>
                                            <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                                            <SelectContent>{categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Prix (HTG) *</Label>
                                        <Input type="number" value={productForm.price} onChange={e => setProductForm({ ...productForm, price: e.target.value })} />
                                        <p className="text-xs text-gray-500">Prix de base pour ce produit.</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Quantité en stock <span className="text-red-500">*</span></Label>
                                        <Input
                                            type="number"
                                            value={productForm.stock_quantity}
                                            onChange={e => setProductForm({ ...productForm, stock_quantity: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Taux de Taxe (%)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="Ex: 10"
                                            value={productForm.tax_rate}
                                            onChange={e => setProductForm({ ...productForm, tax_rate: e.target.value })}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Laissez vide si non applicable.</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>État</Label>
                                        <Select value={productForm.condition} onValueChange={(val) => setProductForm({ ...productForm, condition: val })}>
                                            <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="NEW">Neuf</SelectItem>
                                                <SelectItem value="USED">Occasion (Used)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* SUPPLIER MOQ Section */}
                                    {isSupplierStore && (
                                        <div className="space-y-3 pt-3 border-t">
                                            <div className="flex items-center gap-2">
                                                <Package className="h-4 w-4 text-blue-600" />
                                                <Label className="text-blue-900 font-semibold">Quantité Minimale de Commande (MOQ)</Label>
                                            </div>
                                            {calculateMOQ(productForm.price) !== null ? (
                                                <>
                                                    <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                        <div className="flex-1">
                                                            <div className="text-2xl font-bold text-blue-700">{calculateMOQ(productForm.price)} unités</div>
                                                            <div className="text-xs text-blue-600">Calculé automatiquement selon le prix</div>
                                                        </div>
                                                        <Info className="h-5 w-5 text-blue-400" />
                                                    </div>
                                                    <p className="text-xs text-gray-500">
                                                        🔒 Ce seuil est défini par la plateforme. Si vous n'avez pas cette quantité en stock actuellement, vous pouvez quand même soumettre le produit et nous contacter pour ajuster.
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                                                        <strong>Prix &gt; 5 000 HTG :</strong> Saisie manuelle requise.
                                                    </div>
                                                    <Input
                                                        type="number"
                                                        placeholder="Ex: 20"
                                                        value={productForm.min_wholesale_quantity}
                                                        onChange={e => setProductForm({ ...productForm, min_wholesale_quantity: e.target.value })}
                                                    />
                                                </>
                                            )}
                                            {/* Tier explanation */}
                                            <details className="text-xs text-gray-500 cursor-pointer">
                                                <summary className="font-medium hover:text-gray-700">Voir le tableau des quantités minimales</summary>
                                                <div className="mt-2 overflow-hidden rounded border text-xs">
                                                    <table className="w-full">
                                                        <thead className="bg-gray-100"><tr><th className="p-1 text-left">Prix (HTG)</th><th className="p-1 text-right">MOQ (unités)</th></tr></thead>
                                                        <tbody>
                                                            {[['≤ 50', 1000], ['≤ 100', 800], ['≤ 500', 500], ['≤ 1 000', 450], ['≤ 2 500', 200], ['≤ 5 000', 50], ['> 5 000', 'Manuel']].map(([range, qty]) => (
                                                                <tr key={range} className="border-t"><td className="p-1">{range}</td><td className="p-1 text-right font-medium">{qty}</td></tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* STEP 3: IMAGES & DELIVERY */}
                        {/* STEP 3: RESELL PRICE CONFIG OR STANDARD IMAGES */}
                        {currentStep === 3 && productType === 'resell' && selectedSupplierProduct && (
                            <div className="space-y-6">
                                <div className="flex gap-6">
                                    <div className="w-1/3">
                                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                                            {selectedSupplierProduct.images && selectedSupplierProduct.images[0] ? (
                                                <img src={selectedSupplierProduct.images[0]} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-50"><Package className="h-10 w-10 text-gray-300" /></div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-2/3 space-y-4">
                                        <div>
                                            <h3 className="text-xl font-bold">{selectedSupplierProduct.title}</h3>
                                            <p className="text-gray-500 text-sm line-clamp-3">{selectedSupplierProduct.description}</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 bg-gray-50 rounded-lg border">
                                                <div className="text-sm text-gray-500 mb-1">Prix Fournisseur (Coût)</div>
                                                <div className="text-xl font-bold text-gray-700">{selectedSupplierProduct.wholesale_price ?? selectedSupplierProduct.cost_price ?? selectedSupplierProduct.price ?? 0} HTG</div>
                                            </div>
                                            <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                                                <div className="text-sm text-orange-800 mb-1">Votre Prix de Vente</div>
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        type="number"
                                                        className="font-bold text-lg h-10"
                                                        value={productForm.price}
                                                        onChange={e => setProductForm({ ...productForm, price: e.target.value })}
                                                        placeholder="0.00"
                                                    />
                                                    <span className="font-bold text-orange-800">HTG</span>
                                                </div>
                                            </div>
                                        </div>

                                        {productForm.price && parseFloat(productForm.price) > (selectedSupplierProduct.wholesale_price || selectedSupplierProduct.price || 0) && (
                                            <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                                <TrendingUp className="h-8 w-8 text-green-600" />
                                                <div>
                                                    <div className="font-bold text-green-800">Marge Estimée</div>
                                                    <div className="text-2xl font-bold text-green-600">
                                                        +{(parseFloat(productForm.price) - (selectedSupplierProduct.wholesale_price || selectedSupplierProduct.price || 0)).toFixed(2)} HTG
                                                    </div>
                                                    <div className="text-xs text-green-700">
                                                        Profit par vente (hors frais de plateforme)
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {productForm.price && parseFloat(productForm.price) <= (selectedSupplierProduct.wholesale_price || selectedSupplierProduct.price || 0) && (
                                            <div className="p-3 bg-red-50 text-red-600 text-sm rounded flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4" />
                                                Attention : Vous vendez à perte ou sans profit.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* DELIVERY OVERRIDE SECTION — Revendeurs: livraison VERROUILLÉE par le fournisseur */}
                                <div className="space-y-4 pt-4 border-t">
                                    <h3 className="font-bold flex items-center gap-2">
                                        <Truck className="h-5 w-5 text-gray-900" />
                                        Mode de Livraison
                                    </h3>

                                    {/* Info: revendeurs ne peuvent PAS modifier la livraison */}
                                    <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-sm font-semibold text-blue-900">Livraison gérée par le fournisseur</p>
                                            <p className="text-xs text-blue-700 mt-0.5">
                                                En tant que revendeur, c'est le fournisseur qui expédie. Vous ne pouvez pas modifier les paramètres de livraison pour ce produit.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border opacity-60 cursor-not-allowed">
                                        <input
                                            type="checkbox"
                                            id="overrideShipping"
                                            className="mt-1 cursor-not-allowed"
                                            checked={false}
                                            disabled={true}
                                            onChange={() => { }}
                                        />
                                        <div className="flex-1">
                                            <label htmlFor="overrideShipping" className="font-medium text-gray-500 cursor-not-allowed select-none">
                                                Modifier les frais de livraison (Non disponible)
                                            </label>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Les paramètres de livraison du fournisseur s'appliquent automatiquement.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Show Delivery Inputs if Override is checked OR if we just want to show read-only when unchecked (optional) */}
                                    {/* Actually, let's reuse the existing delivery config fields but enable/disable them */}

                                    <div className={`space-y-4 p-4 rounded-lg border transition-all ${overrideShipping ? 'bg-white border-orange-200 shadow-sm' : 'bg-gray-100 opacity-70 pointer-events-none'}`}>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Mode de Livraison</Label>
                                                <Select
                                                    value={productForm.shipping_type}
                                                    onValueChange={(val) => setProductForm({ ...productForm, shipping_type: val })}
                                                    disabled={!overrideShipping}
                                                >
                                                    <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="STORE_STANDARD">Standard (Poids/Prix)</SelectItem>
                                                        <SelectItem value="STORE_FREE_ZONE">Gratuit (Zone spécifique)</SelectItem>
                                                        <SelectItem value="CARRIER_PAID">Payant (Fixe)</SelectItem>
                                                        <SelectItem value="moresure_client">MoreSure (Client paie)</SelectItem>
                                                        <SelectItem value="moresure_free">MoreSure (Vendeur paie)</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Poids (Lbs)</Label>
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        type="number"
                                                        readOnly
                                                        className="bg-gray-50 cursor-not-allowed"
                                                        placeholder="0.00"
                                                        value={selectedSupplierProduct.delivery_settings?.weight || productForm.weight || 0}
                                                    />
                                                </div>
                                                <p className="text-[10px] text-gray-500">Le poids est défini par le fournisseur.</p>
                                            </div>
                                        </div>
                                        {productForm.shipping_type === 'CARRIER_PAID' && (
                                            <div className="space-y-2">
                                                <Label>Prix Livraison (HTG)</Label>
                                                <Input
                                                    type="number"
                                                    disabled={!overrideShipping}
                                                    placeholder="0.00"
                                                    value={productForm.shipping_price}
                                                    onChange={(e) => setProductForm({ ...productForm, shipping_price: e.target.value })}
                                                />
                                            </div>
                                        )}
                                        {["moresure_client", "moresure_free"].includes(productForm.shipping_type) && (
                                            <div className="text-xs text-blue-600 p-2 bg-blue-50 rounded">
                                                Info: {moresureEstimate ? `Estimation: ${moresureEstimate} HTG` : "Calcul auto au checkout"}
                                            </div>
                                        )}
                                    </div>

                                    {/* Returns Option (Resell) */}
                                    <div className="pt-4 border-t space-y-4">
                                        <div className={`border p-3 rounded flex items-center gap-2 cursor-pointer transition-all ${(productForm.return_policy_days > 0) ? 'border-green-500 bg-green-50' : 'hover:bg-gray-50'}`} onClick={() => setProductForm({ ...productForm, return_policy_days: productForm.return_policy_days > 0 ? 0 : 3 })}>
                                            <div className={`flex items-center justify-center w-5 h-5 rounded border ${(productForm.return_policy_days > 0) ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 bg-white'}`}>
                                                {(productForm.return_policy_days > 0) && <Check className="h-3.5 w-3.5 bold" />}
                                            </div>
                                            <RefreshCcw className="h-5 w-5 text-green-600" />
                                            <div className="flex-1">
                                                <div className="font-bold flex items-center justify-between">
                                                    Accepter les retours
                                                    {(productForm.return_policy_days > 0) && <Badge className="bg-green-600 text-[10px] h-4">ACTIVÉ</Badge>}
                                                </div>
                                                <div className="text-xs text-gray-500">Permettre aux acheteurs de retourner cet article sous un certain délai</div>
                                            </div>
                                        </div>

                                        {(productForm.return_policy_days > 0) && (
                                            <div className="space-y-2 pl-10">
                                                <Label className="text-green-800">Délai de retour (en jours) *</Label>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={productForm.return_policy_days || ''}
                                                    onChange={e => setProductForm({ ...productForm, return_policy_days: parseInt(e.target.value) || 0 })}
                                                    className="max-w-[150px] border-green-300 focus-visible:ring-green-500"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 3 && productType !== 'resell' && (
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <Label>Images</Label>
                                    <div className="flex gap-2">
                                        <Input placeholder="URL image" value={tempImageUrl} onChange={(e) => setTempImageUrl(e.target.value)} />
                                        <Button onClick={handleAddImage} variant="secondary">Ajouter</Button>
                                        <div className="relative">
                                            <input type="file" id="main-image-upload" className="hidden" accept="image/png, image/jpeg, image/gif" onChange={(e) => handleFileUpload(e, "main")} />
                                            <Button type="button" variant="outline" onClick={() => document.getElementById('main-image-upload').click()}><Upload className="h-4 w-4 mr-2" /> Upload</Button>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {productForm.images.map((img, idx) => (
                                            <div key={idx} className="relative w-24 h-24 border rounded overflow-hidden group">
                                                <img src={img} alt="" className="w-full h-full object-cover" />
                                                <button onClick={() => handleRemoveImage(idx)} className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100"><X size={12} /></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* NEW DELIVERY CONFIGURATION */}
                                <div className="border-t pt-4">
                                    <Label className="text-lg mb-2 block">Option de Livraison</Label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Main Shipping Options (Radio-like behavior) */}
                                        <div className={`border p-3 rounded flex items-center gap-2 cursor-pointer ${productForm.shipping_type === 'STORE_STANDARD' ? 'border-orange-500 bg-orange-50' : ''}`} onClick={() => setProductForm({ ...productForm, shipping_type: 'STORE_STANDARD' })}>
                                            <Truck className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <div className="font-bold">Standard Boutique</div>
                                                <div className="text-xs text-gray-500">Calculé selon poids/distance (paramètres boutique)</div>
                                            </div>
                                        </div>
                                        <div className={`border p-3 rounded flex items-center gap-2 cursor-pointer ${productForm.shipping_type === 'STORE_FREE_ZONE' ? 'border-orange-500 bg-orange-50' : ''}`} onClick={() => setProductForm({ ...productForm, shipping_type: 'STORE_FREE_ZONE' })}>
                                            <MapPin className="h-5 w-5 text-green-500" />
                                            <div>
                                                <div className="font-bold">Gratuit Zone Proche</div>
                                                <div className="text-xs text-gray-500">Gratuit si proche, sinon Standard (-100%)</div>
                                            </div>
                                        </div>
                                        <div className={`border p-3 rounded flex items-center gap-2 cursor-pointer ${productForm.shipping_type === 'CARRIER_PAID' ? 'border-orange-500 bg-orange-50' : ''}`} onClick={() => setProductForm({ ...productForm, shipping_type: 'CARRIER_PAID' })}>
                                            <DollarSign className="h-5 w-5 text-blue-500" />
                                            <div>
                                                <div className="font-bold">Transporteur (Payant)</div>
                                                <div className="text-xs text-gray-500">Prix fixe défini ici</div>
                                            </div>
                                        </div>
                                        <div className={`border p-3 rounded flex items-center gap-2 cursor-pointer ${productForm.shipping_type === 'CARRIER_MARKETING' ? 'border-orange-500 bg-orange-50' : ''}`} onClick={() => setProductForm({ ...productForm, shipping_type: 'CARRIER_MARKETING' })}>
                                            <Gift className="h-5 w-5 text-purple-500" />
                                            <div>
                                                <div className="font-bold">Transporteur (Offert)</div>
                                                <div className="text-xs text-gray-500">Gratuit pour le client (À vos frais)</div>
                                            </div>
                                        </div>

                                        {/* Pickup Option (Cumulative) */}
                                        <div className={`border p-3 rounded flex items-center gap-2 cursor-pointer md:col-span-2 transition-all ${productForm.allow_pickup ? 'border-amber-500 bg-amber-50' : 'hover:bg-gray-50'}`} onClick={() => setProductForm({ ...productForm, allow_pickup: !productForm.allow_pickup })}>
                                            <div className={`flex items-center justify-center w-5 h-5 rounded border ${productForm.allow_pickup ? 'border-amber-500 bg-amber-500 text-white' : 'border-gray-300 bg-white'}`}>
                                                {productForm.allow_pickup && <Check className="h-3.5 w-3.5 bold" />}
                                            </div>
                                            <ShoppingCart className="h-5 w-5 text-amber-600" />
                                            <div className="flex-1">
                                                <div className="font-bold flex items-center justify-between">
                                                    Retrait en Boutique (Pickup)
                                                    {productForm.allow_pickup && <Badge className="bg-amber-600 text-[10px] h-4">ACTIVÉ</Badge>}
                                                </div>
                                                <div className="text-xs text-gray-500">L'acheteur peut venir récupérer sa commande sur place (Gratuit)</div>
                                            </div>
                                        </div>

                                        {/* Express Option (Cumulative) */}
                                        <div className={`border p-3 rounded flex items-center gap-2 cursor-pointer md:col-span-2 transition-all ${productForm.allow_express ? 'border-orange-500 bg-orange-50' : 'hover:bg-gray-50'}`} onClick={() => setProductForm({ ...productForm, allow_express: !productForm.allow_express })}>
                                            <div className={`flex items-center justify-center w-5 h-5 rounded border ${productForm.allow_express ? 'border-orange-500 bg-orange-500 text-white' : 'border-gray-300 bg-white'}`}>
                                                {productForm.allow_express && <Check className="h-3.5 w-3.5 bold" />}
                                            </div>
                                            <Zap className="h-5 w-5 text-orange-600" />
                                            <div className="flex-1">
                                                <div className="font-bold flex items-center justify-between">
                                                    Disponible en Express (24H)
                                                    {productForm.allow_express && <Badge className="bg-orange-600 text-[10px] h-4">ACTIVÉ</Badge>}
                                                </div>
                                                <div className="text-xs text-gray-500">Activer la livraison Express pour ce produit (Utilise les paramètres Express de votre boutique)</div>
                                            </div>
                                        </div>
                                        {/* Returns Option */}
                                        <div className={`border p-3 rounded flex items-center gap-2 cursor-pointer md:col-span-2 transition-all ${(productForm.return_policy_days > 0) ? 'border-green-500 bg-green-50' : 'hover:bg-gray-50'}`} onClick={() => setProductForm({ ...productForm, return_policy_days: productForm.return_policy_days > 0 ? 0 : 3 })}>
                                            <div className={`flex items-center justify-center w-5 h-5 rounded border ${(productForm.return_policy_days > 0) ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 bg-white'}`}>
                                                {(productForm.return_policy_days > 0) && <Check className="h-3.5 w-3.5 bold" />}
                                            </div>
                                            <RefreshCcw className="h-5 w-5 text-green-600" />
                                            <div className="flex-1">
                                                <div className="font-bold flex items-center justify-between">
                                                    Accepter les retours
                                                    {(productForm.return_policy_days > 0) && <Badge className="bg-green-600 text-[10px] h-4">ACTIVÉ</Badge>}
                                                </div>
                                                <div className="text-xs text-gray-500">Permettre aux acheteurs de retourner cet article sous un certain délai</div>
                                            </div>
                                        </div>

                                        {(productForm.return_policy_days > 0) && (
                                            <div className="md:col-span-2 space-y-2 pl-10 mb-2">
                                                <Label className="text-green-800">Délai de retour (en jours) *</Label>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={productForm.return_policy_days || ''}
                                                    onChange={e => setProductForm({ ...productForm, return_policy_days: parseInt(e.target.value) || 0 })}
                                                    className="max-w-[150px] border-green-300 focus-visible:ring-green-500"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mt-4">
                                        {(productForm.shipping_type === 'STORE_STANDARD' || productForm.shipping_type === 'STORE_FREE_ZONE') && (
                                            <div className="space-y-2">
                                                <Label>Poids (Lbs) *Requis</Label>
                                                <Input type="number" value={productForm.weight} onChange={e => setProductForm({ ...productForm, weight: e.target.value })} placeholder="Ex: 0.5" />
                                            </div>
                                        )}
                                        {(productForm.shipping_type === 'CARRIER_PAID') && (
                                            <div className="space-y-2">
                                                <Label>Prix de Livraison (HTG) *Requis</Label>
                                                <Input type="number" value={productForm.shipping_price} onChange={e => setProductForm({ ...productForm, shipping_price: e.target.value })} placeholder="Ex: 250" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 4 && (
                            <div className="space-y-4">
                                {productType === 'variable' ? (
                                    <div className="space-y-4">
                                        <Label>Configuration des Variantes</Label>
                                        <p className="text-xs text-gray-500 mb-2">Ajoutez des attributs (ex: Taille, Couleur) et leurs valeurs (ex: S, M, L) pour générer les combinaisons.</p>
                                        <div className="flex gap-2">
                                            <Input placeholder="Taille" value={attrName} onChange={e => setAttrName(e.target.value)} />
                                            <Input placeholder="S, M, L" value={attrValues} onChange={e => setAttrValues(e.target.value)} />
                                            <Button onClick={addAttribute}>Ajouter</Button>
                                        </div>

                                        {productForm.attributes.length > 0 && (
                                            <div className="flex flex-wrap gap-2 py-2">
                                                {productForm.attributes.map((attr, idx) => (
                                                    <Badge key={idx} variant="secondary" className="gap-1 px-2 py-1">
                                                        <span className="font-bold">{attr.name}:</span>
                                                        <span className="max-w-[150px] truncate">{attr.values.join(', ')}</span>
                                                        <button onClick={() => removeAttribute(idx)} className="ml-1 hover:text-red-500">
                                                            <X size={12} />
                                                        </button>
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}

                                        <Button onClick={generateVariants} variant="outline" className="w-full">Générer</Button>
                                        {productForm.variants.length > 0 && (
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Variante</TableHead>
                                                        <TableHead>Prix (HTG)</TableHead>
                                                        <TableHead>Stock</TableHead>
                                                        <TableHead>Images</TableHead>
                                                        <TableHead className="w-10"></TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {productForm.variants.map((v, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell className="font-medium">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {Object.keys(v.attributes).map(attrName => (
                                                                        <div key={attrName} className="flex flex-col gap-0.5">
                                                                            <span className="text-[10px] text-gray-400 font-normal">{attrName}</span>
                                                                            <Input
                                                                                value={v.attributes[attrName]}
                                                                                onChange={(e) => {
                                                                                    const updatedVariants = [...productForm.variants];
                                                                                    updatedVariants[i].attributes[attrName] = e.target.value;
                                                                                    setProductForm(prev => ({ ...prev, variants: updatedVariants }));
                                                                                }}
                                                                                className="h-8 w-20 text-xs px-2"
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Input
                                                                    type="number"
                                                                    value={v.price}
                                                                    onChange={e => updateVariant(i, 'price', e.target.value)}
                                                                    className="w-24"
                                                                    placeholder="Prix"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Input
                                                                    type="number"
                                                                    value={v.stock}
                                                                    onChange={e => updateVariant(i, 'stock', e.target.value)}
                                                                    className="w-20"
                                                                    placeholder="Qté"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Button size="sm" variant={v.images?.length > 0 ? "secondary" : "outline"} onClick={() => handleOpenVariantImages(i)}>
                                                                    <ImageIcon className="h-4 w-4 mr-2" />
                                                                    {v.images?.length || 0}/5
                                                                </Button>
                                                            </TableCell>
                                                            <TableCell>
                                                                <button onClick={() => setProductForm(prev => ({ ...prev, variants: prev.variants.filter((_, idx) => idx !== i) }))} className="text-red-500 hover:text-red-700">
                                                                    <Trash2 className="h-4 w-4" />
                                                                </button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </div>
                                ) : productType === 'resell' ? (
                                    <div className="space-y-6">
                                        <div className="text-center">
                                            <h3 className="text-xl font-bold mb-2">Prévisualisation</h3>
                                            <p className="text-gray-500">Voici comment votre produit apparaîtra dans votre boutique.</p>
                                        </div>

                                        <Card className="max-w-sm mx-auto overflow-hidden shadow-lg">
                                            <div className="aspect-square bg-gray-100 relative">
                                                {productForm.images && productForm.images[0] && (
                                                    <img src={productForm.images[0]} alt={productForm.title} className="w-full h-full object-cover" />
                                                )}
                                                <Badge className="absolute top-2 right-2 bg-orange-600">Nouveau</Badge>
                                            </div>
                                            <CardContent className="p-4">
                                                <h4 className="font-bold text-lg mb-1 line-clamp-1">{productForm.title}</h4>
                                                <div className="flex justify-between items-end">
                                                    <div>
                                                        <div className="text-2xl font-bold text-gray-900">{productForm.price} HTG</div>
                                                        <div className="text-xs text-gray-500">Vendu par : {store?.name}</div>
                                                    </div>
                                                </div>
                                                <div className="mt-4 pt-4 border-t flex justify-between text-sm">
                                                    <span className="text-gray-500">Marge par vente :</span>
                                                    <span className="font-bold text-green-600">
                                                        +{((parseFloat(productForm.price) || 0) - (productForm.cost_price || 0)).toFixed(2)} HTG
                                                    </span>
                                                </div>
                                            </CardContent>
                                        </Card>

                                        <div className="bg-blue-50 p-4 rounded-lg flex gap-3 items-start border border-blue-100">
                                            <Truck className="h-5 w-5 text-blue-600 mt-0.5" />
                                            <div className="text-sm text-blue-800">
                                                <strong>Livraison Dropshipping :</strong> La livraison est gérée automatiquement depuis les stocks du fournisseur. Vous n'avez rien à expédier.
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-10">
                                        <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                                        <h3 className="text-xl font-bold">Tout est bon !</h3>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                            disabled={currentStep === 1 || (isEditing && productType === 'resell' && currentStep <= 3) || (isEditing && productType !== 'resell' && currentStep <= 2)}
                        >
                            Précédent
                        </Button>
                        {(currentStep < 4 && !(productType === 'resell' && currentStep === 3) && !(productType === 'simple' && currentStep === 3)) ?
                            <Button onClick={handleNextStep}>Suivant</Button>
                            :
                            <Button onClick={handleCreateOrUpdateProduct}>{isEditing ? "Enregistrer" : "Publier"}</Button>
                        }
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ORDER DETAILS MODAL */}
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader className="flex flex-row justify-between items-center pr-8">
                        <DialogTitle>Détails Commande #{selectedOrder?.order_number}</DialogTitle>
                        {selectedOrder && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadLabel(selectedOrder.id)}
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                Bon de Livraison
                            </Button>
                        )}
                    </DialogHeader>
                    {selectedOrder && (
                        <div className="space-y-6">
                            {/* Header Info */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm border-b pb-4">
                                <div>
                                    <h4 className="font-semibold text-gray-700 flex items-center gap-1"><User className="h-4 w-4" /> Client</h4>
                                    <p className="font-medium">{selectedOrder.customer_name || selectedOrder.shipping_address?.name || 'Nom inconnu'}</p>
                                    <p className="text-gray-500">{selectedOrder.customer_email || selectedOrder.shipping_address?.email}</p>
                                    <p className="text-gray-500">{selectedOrder.customer_phone || selectedOrder.shipping_address?.phone || 'Téléphone non fourni'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-700 flex items-center gap-1"><MapPin className="h-4 w-4" /> Livraison</h4>
                                    <p>{selectedOrder.shipping_address?.address}</p>
                                    <p>{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.country}</p>
                                    {selectedOrder.shipping_address?.coordinates && (
                                        <a href={`https://www.google.com/maps?q=${selectedOrder.shipping_address.coordinates.lat},${selectedOrder.shipping_address.coordinates.lng}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1 mt-1">
                                            <MapPin className="h-3 w-3" /> Voir sur GPS
                                        </a>
                                    )}
                                </div>
                                <div className="bg-orange-50 p-3 rounded-lg border border-orange-100 shadow-sm">
                                    <h4 className="font-semibold text-orange-800 flex items-center gap-1"><Truck className="h-4 w-4" /> Logistique</h4>
                                    <div className="space-y-1 mt-1">
                                        <p className="text-[11px] flex justify-between">
                                            <span className="text-gray-500 uppercase">Mode:</span>
                                            <span className="font-bold text-orange-700">{selectedOrder.items?.find(i => i.store_id === storeId)?.delivery_mode || 'Standard'}</span>
                                        </p>
                                        <p className="text-[11px] flex justify-between">
                                            <span className="text-gray-500 uppercase">Délai estimé:</span>
                                            <span className="font-bold">{selectedOrder.shipping_info?.delivery_days || '3-5'} jours</span>
                                        </p>
                                        <p className="text-[11px] flex justify-between">
                                            <span className="text-gray-500 uppercase">Paiement:</span>
                                            <span className="font-bold">{selectedOrder.payment_method === 'cash_on_delivery' ? 'À la livraison' : 'Déjà payé (Wallet/MonCash)'}</span>
                                        </p>
                                        <div className="mt-2 pt-2 border-t border-orange-200">
                                            <p className="text-xs font-bold text-orange-800 flex flex-col">
                                                <span className="text-[9px] uppercase opacity-60">Date Limite de Livraison:</span>
                                                {(() => {
                                                    const date = new Date(selectedOrder.created_at);
                                                    const days = parseInt(selectedOrder.shipping_info?.delivery_days || 5);
                                                    date.setDate(date.getDate() + days);
                                                    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                                                })()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Order Items Table */}
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Produit</TableHead>
                                        <TableHead>Qté</TableHead>
                                        <TableHead>Prix</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {selectedOrder.items.filter(i => i.store_id === storeId).map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="font-medium">{item.product_title}</TableCell>
                                            <TableCell>{item.quantity}</TableCell>
                                            <TableCell>{item.unit_price} HTG</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={
                                                    ['DELIVERED', 'COMPLETED', 'DELIVERY_CONFIRMED'].includes(item.delivery_status) ? 'bg-green-100 text-green-800' :
                                                        item.delivery_status === 'SHIPPED' ? 'bg-blue-100 text-blue-800' :
                                                            ['CANCELLED', 'REFUNDED'].includes(item.delivery_status) ? 'bg-red-100 text-red-800 border-red-200' :
                                                                'bg-yellow-100 text-yellow-800'
                                                }>
                                                    {item.delivery_status === 'PENDING'   ? 'À Traiter' :
                                                     item.delivery_status === 'PREPARING' ? '🚦 Prêt — En attente livreur' :
                                                     item.delivery_status === 'SHIPPED'   ? '📦 Expédié' :
                                                     item.delivery_status === 'CANCELLED' ? 'Annulé' :
                                                     item.delivery_status}
                                                </Badge>
                                                {item.tracking_number && <div className="text-xs text-gray-500 mt-1">Track: {item.tracking_number}</div>}
                                            </TableCell>
                                            <TableCell>
                                                {['PENDING', 'PROCESSING'].includes(item.delivery_status) && (
                                                    <div className="flex flex-col gap-2">
                                                        {/* TRANSPORT/EXPRESS : passe par TransporteurPro (pas de SHIPPED direct) */}
                                                        {['TRANSPORT', 'EXPRESS'].includes(item.delivery_mode) ? (
                                                            <Button size="sm" variant="secondary"
                                                                onClick={() => handleMarkReady(selectedOrder.id)}>
                                                                📦 Colis Prêt
                                                            </Button>
                                                        ) : (
                                                            /* Autres modes (PICKUP, STORE_DELIVERY) : flux manuel */
                                                            <Button size="sm" variant="secondary"
                                                                onClick={() => handleOpenTrackingDialog(item)}>
                                                                Expédier
                                                            </Button>
                                                        )}
                                                        <Button size="sm" variant="destructive" onClick={() => handleRefuseItem(item.id)}>
                                                            Refuser
                                                        </Button>
                                                    </div>
                                                )}
                                                {/* PREPARING : livreur pas encore arrivé — pas d'action vendeur */}
                                                {item.delivery_status === 'PREPARING' && (
                                                    <span className="text-xs text-amber-600 font-medium">
                                                        En attente du livreur
                                                    </span>
                                                )}
                                                {/* SHIPPED → "Marquer Livré" :
                                                    - STORE_DELIVERY / PICKUP : vendeur livre lui-même → bouton toujours disponible
                                                    - TRANSPORT / EXPRESS      : bloqué jusqu'à confirmation Sure More */}
                                                {item.delivery_status === 'SHIPPED' && (() => {
                                                    const sellerModes = ['STORE_DELIVERY', 'PICKUP', 'SELLER'];
                                                    const isSellerDelivery = sellerModes.includes((item.delivery_mode || '').toUpperCase());
                                                    if (!isSellerDelivery) {
                                                        const ms = selectedOrder.shipping_info?.[storeId]?.transporteur_status;
                                                        if (ms && ms !== 'delivered') return null;
                                                    }
                                                    return (
                                                        <Button size="sm" variant="outline" onClick={() => handleUpdateItemStatus(item.id, 'DELIVERED')}>
                                                            Marquer Livré
                                                        </Button>
                                                    );
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {/* Order Financial Summary */}
                            {(() => {
                                const storeItems = selectedOrder.items.filter(i => i.store_id === storeId);
                                const itemsTotal = storeItems.reduce((acc, i) => acc + (i.unit_price * i.quantity), 0);
                                const shippingInfo = selectedOrder.shipping_info?.[storeId];
                                const shippingCost = parseFloat(shippingInfo?.price || 0);
                                const travelFee = parseFloat(selectedOrder.travel_fee || 0);
                                return (
                                    <div className="border-t pt-3 space-y-1 text-sm text-right">
                                        <div className="flex justify-between text-gray-600">
                                            <span>Sous-total produits</span>
                                            <span className="font-medium">{itemsTotal.toFixed(2)} HTG</span>
                                        </div>
                                        {shippingCost > 0 && (
                                            <div className="flex justify-between text-gray-600">
                                                <span className="flex items-center gap-1">
                                                    <Truck className="h-3.5 w-3.5" /> Livraison
                                                </span>
                                                <span className="font-medium">{shippingCost.toFixed(2)} HTG</span>
                                            </div>
                                        )}
                                        {travelFee > 0 && (
                                            <div className="flex justify-between text-gray-600">
                                                <span className="flex items-center gap-1">
                                                    <MapPin className="h-3.5 w-3.5" /> Frais de déplacement
                                                </span>
                                                <span className="font-medium">{travelFee.toFixed(2)} HTG</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between font-bold text-base border-t pt-2 mt-1">
                                            <span>Total commande</span>
                                            <span className="text-orange-700">{(itemsTotal + shippingCost + travelFee).toFixed(2)} HTG</span>
                                        </div>
                                    </div>
                                );
                            })()}

                            <DialogFooter className="flex justify-between sm:justify-between items-center w-full">
                                <Button variant="outline" onClick={handlePrintDeliverySlip}>
                                    <Printer className="mr-2 h-4 w-4" /> Imprimer Bon de Livraison
                                </Button>
                                <Button onClick={() => setDetailsOpen(false)}>Fermer</Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* TRACKING NUMBER DIALOG */}
            <Dialog open={trackingDialogOpen} onOpenChange={setTrackingDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Truck className="h-5 w-5 text-orange-500" />
                            Expédier l'article
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3">
                            <p className="text-sm font-semibold text-orange-800">
                                Êtes-vous sûr de vouloir expédier cet article ?
                            </p>
                            <p className="text-xs text-orange-600 mt-1">
                                Cette action notifie l'équipe de livraison et ne peut pas être annulée.
                            </p>
                        </div>
                        <div>
                            <Label className="text-sm text-gray-600">Numéro de suivi (Optionnel)</Label>
                            <Input
                                className="mt-1.5"
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                placeholder="Ex: TRK-123456789"
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setTrackingDialogOpen(false)}>Annuler</Button>
                        <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleSubmitTracking}>
                            <Truck className="h-4 w-4 mr-1.5" /> Confirmer Expédition
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* CONFIRMATION EXPÉDITION */}
            {/* PRODUCT DETAILS DIALOG */}
            <Dialog open={!!viewProduct} onOpenChange={(open) => !open && setViewProduct(null)}>
                <DialogContent className="max-w-3xl overflow-y-auto max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl">
                            <Info className="h-5 w-5 text-blue-600" />
                            Détails du Produit
                        </DialogTitle>
                    </DialogHeader>

                    {viewProduct && (
                        <div className="space-y-6">
                            {/* Header Info */}
                            <div className="flex gap-6">
                                <div className="w-32 h-32 rounded-lg bg-gray-100 overflow-hidden border border-gray-200 shrink-0">
                                    {viewProduct.images && viewProduct.images.length > 0 ? (
                                        <img src={viewProduct.images[0]} alt={viewProduct.title} className="w-full h-full object-cover" />
                                    ) : (
                                        <Package className="w-full h-full p-8 text-gray-300" />
                                    )}
                                </div>
                                <div className="space-y-2 flex-1">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">{viewProduct.title}</h3>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Badge variant="outline">{viewProduct.product_type === 'PHYSICAL' ? 'Physique' : viewProduct.product_type}</Badge>
                                                {viewProduct.is_resell && <Badge className="bg-blue-100 text-blue-800 border-blue-200">Revente</Badge>}
                                                {viewProduct.has_variants && <Badge className="bg-orange-100 text-orange-800 border-orange-200">Variable</Badge>}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-bold text-gray-900">{viewProduct.price} HTG</div>
                                            {viewProduct.discount_price && (
                                                <div className="text-sm text-red-500 line-through">{viewProduct.discount_price} HTG</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                        <div className="bg-gray-50 p-2 rounded border border-gray-100">
                                            <span className="text-xs text-gray-500 block uppercase tracking-wider">Stock Total</span>
                                            <span className="font-mono font-bold">{viewProduct.stock_quantity}</span>
                                        </div>
                                        <div className="bg-gray-50 p-2 rounded border border-gray-100">
                                            <span className="text-xs text-gray-500 block uppercase tracking-wider">Statut</span>
                                            <span className={`font-medium ${viewProduct.status === 'ACTIVE' ? 'text-green-600' : 'text-gray-500'}`}>
                                                {viewProduct.status === 'ACTIVE' ? 'Actif' : 'Brouillon'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2 border-b pb-1">Description</h4>
                                <p className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded-md min-h-[4rem]">
                                    {viewProduct.description || "Aucune description."}
                                </p>
                            </div>

                            {/* Variants Table */}
                            {viewProduct.has_variants && viewProduct.variants && (
                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2 border-b pb-1 flex items-center gap-2">
                                        <Layers className="h-4 w-4" /> Variantes ({viewProduct.variants.length})
                                    </h4>
                                    <div className="border rounded-md overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-gray-50 hover:bg-gray-50">
                                                    <TableHead className="w-16">Image</TableHead>
                                                    <TableHead>Attributs</TableHead>
                                                    <TableHead className="text-right">Prix</TableHead>
                                                    <TableHead className="text-right">Stock</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {viewProduct.variants.map((variant, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell className="py-2">
                                                            <div className="w-8 h-8 rounded bg-gray-100 overflow-hidden border">
                                                                {variant.images && variant.images.length > 0 ? (
                                                                    <img src={variant.images[0]} alt="" className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <div className="w-full h-full bg-gray-200" />
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="py-2 text-sm font-medium">
                                                            {variant.attributes ?
                                                                Object.entries(variant.attributes).map(([k, v]) => `${k}: ${v}`).join(', ')
                                                                : 'N/A'}
                                                        </TableCell>
                                                        <TableCell className="py-2 text-right text-sm">{variant.price || viewProduct.price} HTG</TableCell>
                                                        <TableCell className="py-2 text-right font-mono text-sm">{variant.stock}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {/* Resell Info */}
                            {viewProduct.is_resell && (
                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2 border-b pb-1 flex items-center gap-2">
                                        <RefreshCcw className="h-4 w-4" /> Infos Dropshipping
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4 bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
                                        <div>
                                            <span className="text-blue-600 font-medium block">ID Fournisseur</span>
                                            <span className="text-gray-900 font-mono text-xs">{viewProduct.supplier_store_id || "N/A"}</span>
                                        </div>
                                        <div>
                                            <span className="text-blue-600 font-medium block">ID Produit Original</span>
                                            <span className="text-gray-900 font-mono text-xs">{viewProduct.original_product_id || "N/A"}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button onClick={() => setViewProduct(null)} variant="outline">Fermer</Button>
                        {viewProduct && (
                            <Button onClick={() => { setViewProduct(null); handleEditProduct(viewProduct); }}>
                                <Edit className="mr-2 h-4 w-4" /> Modifier ce produit
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* RETURN PROCESS DIALOG */}
            <Dialog open={returnProcessOpen} onOpenChange={setReturnProcessOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Traiter le Retour {selectedReturn?.return_code}</DialogTitle>
                    </DialogHeader>
                    {selectedReturn && (
                        <div className="space-y-6 py-4">
                            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg border">
                                <div className="w-16 h-16 rounded bg-white overflow-hidden border shrink-0">
                                    {selectedReturn.product_image ? (
                                        <img src={selectedReturn.product_image} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <Package className="w-8 h-8 m-4 text-gray-300" />
                                    )}
                                </div>
                                <div>
                                    <h4 className="font-semibold">{selectedReturn.product_title}</h4>
                                    <p className="text-sm text-gray-500">Montant payé: <span className="font-bold text-gray-900">{selectedReturn.unit_price * selectedReturn.quantity} HTG</span></p>
                                    <p className="text-sm text-gray-500">Quantité: {selectedReturn.quantity}</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <Label>Action</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    value={returnAction}
                                    onChange={(e) => setReturnAction(e.target.value)}
                                >
                                    <option value="ACCEPT">Accepter & Rembourser Intégralement</option>
                                    <option value="PARTIAL_REFUND">Proposer Remboursement Partiel</option>
                                    <option value="REJECT">Refuser & Ouvrir un Litige</option>
                                </select>
                            </div>

                            {returnAction === "PARTIAL_REFUND" && (
                                <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                                    <Label>Montant du remboursement (HTG)</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max={selectedReturn.unit_price * selectedReturn.quantity}
                                        value={returnAmount}
                                        onChange={(e) => setReturnAmount(e.target.value)}
                                        placeholder={`Ex: ${(selectedReturn.unit_price * selectedReturn.quantity * 0.5).toFixed(2)}`}
                                    />
                                    <p className="text-xs text-gray-500">L'acheteur devra accepter cette offre de remboursement partiel. Le montant maximum est de {selectedReturn.unit_price * selectedReturn.quantity} HTG.</p>
                                </div>
                            )}

                            {returnAction === "REJECT" && (
                                <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                                    <Label>Motif du refus (Litige)</Label>
                                    <Textarea
                                        value={returnReason}
                                        onChange={(e) => setReturnReason(e.target.value)}
                                        placeholder="Veuillez expliquer pourquoi vous refusez ce retour..."
                                        rows={4}
                                    />
                                    <p className="text-xs text-gray-500">Ce refus enverra automatiquement le dossier en litige auprès de l'administration Buymore.</p>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReturnProcessOpen(false)}>Annuler</Button>
                        <Button
                            onClick={handleProcessReturn}
                            variant={returnAction === "REJECT" ? "destructive" : "default"}
                        >
                            Confirmer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
